import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  useNavigate,
  Routes,
  Route,
  useLocation,
  useNavigationType,
  Navigate,
} from 'react-router-dom';
import axios from 'axios';
import { chain, cloneDeep, debounce, isEmpty, sortBy, uniqueId } from 'lodash';
import moment from 'moment';

import OrderTypeSelection from '../../components/RestaurantMenu/OrderTypeSelection';
import {
  THEATER_TILE,
  TICKETS_TILE,
  bagTakeAwayPriceFor,
  orderTypeForTile,
  buildOrderTypeOptions,
  translateOrderTypeOptions,
  withTheaterAvailability,
  withClosedState,
  isTheaterOrderNowFlow,
  sendsTheaterPerformance,
  theaterOrderFields,
  venueSellsOnline,
} from '../../utils/orderTypes';
import RestaurantMenuHeader from '../../components/RestaurantMenu/RestaurantMenuHeader';
import MenuGrid from '../../components/RestaurantMenu/MenuGrid';
import OrderSummary from '../../components/RestaurantMenu/OrderSummary';
import OrderInformation from '../../components/RestaurantMenu/OrderInformation';
import GuestInformation from '../../components/RestaurantMenu/GuestInformation';
import YourItems from '../../components/RestaurantMenu/YourItems';
import StampProgressCard from '../../components/RestaurantMenu/StampProgressCard';
import useStockCheck, { checkStockForAdd } from '../../hooks/useStockCheck';
import {
  discountMessage,
  useDiscountCodeLookup,
} from '../../components/RestaurantMenu/DiscountCodeBlock';
import Payment from '../../components/RestaurantMenu/Payment';
import ProcessingOrderModal from '../../components/RestaurantMenu/ProcessingOrderModal.js';
import RestaurantAvailabilityModal from '../../components/RestaurantMenu/RestaurantAvailabilityModal.js';
import MenuNotAvailableModal from '../../components/RestaurantMenu/MenuNotAvailableModal';
import OrderNotAllowedModal from '../../components/RestaurantMenu/OrderNotAllowedModal';
import RushModeModal from '../../components/RestaurantMenu/RushModeModal.js';
import PromoPopupModal from '../../components/RestaurantMenu/PromoPopupModal.js';
import ValidationErrorModal from '../../components/RestaurantMenu/ValidationErrorModal.js';
import DeleteCardModal from '../../components/DeleteCardModal.js';
import NestedScrollSpyTabs from '../../components/NestedScrollSpyTabs';
import Loader from '../../components/Loader.js';
import { Modal } from '../../components/ui/Modal';
import { MultiSelectCheckbox } from '../../components/ui/MultiSelectCheckbox';
import { MultiCounter } from '../../components/ui/MultiCounter';
import { Counter } from '../../components/ui/Counter';
import { RadioGroup } from '../../components/ui/RadioGroup';
import ErrorModal from '../../components/RestaurantMenu/ErrorModal.js';

import { readPendingOrderForVenue, receiptPerformance } from '../../utils/pendingOrder';
import configData from '../../config.json';
import {
  getRestaurantStatus,
  getNextOpeningSlot,
  isCategoryAvailable,
  getDisabledTimes,
  getEarliestTime,
  isHigherCategoryOpenAt,
  isHigherCategoryServedOn,
} from '../../utils/restaurant-availability.js';
import { computeDiscountedTotal } from '../../utils/stampcard';
import { defaultPickupMode, firstPickupSlot } from '../../utils/pickupSlots';
import {
  checkWoltMinOrder,
  woltMinOrderRejection,
  woltMinOrderValue,
} from '../../utils/woltMinOrder';
import {
  NO_WOLT_ERROR,
  isWoltFeePending,
  isWoltQuoteFresh,
  woltClosedRejection,
  woltOrderFields,
  woltQuoteErrorMessage,
  woltQuoteFee,
  woltQuoteSlot,
} from '../../utils/woltDelivery';
import { markDeliveryOrder } from '../../utils/deliveryTracking';
import { renderCurrencyLanguage } from '../../utils';
import { useDataContext } from '../../context/data.js';
import { useOrderContext } from '../../context/order.js';
import { useNotificationContext } from '../../context/notification.js';
import { useRestaurantContext } from '../../context/restaurants.js';
import { useStampWallet } from '../../context/stampWallet.js';

import translations from '../../constants/translations.json';
import { isBlockedRestaurant } from '../../constants/blockedRestaurants';
import CapacityFullModal from '../../components/RestaurantMenu/CapacityFullModal';

import { useOrderStatusPolling } from '../../hooks/useOrderStatusPolling.js';
import { usePreorderPersistence } from '../../hooks/usePreorderPersistence.js';
import { useLagTime } from '../../hooks/useLagTime.js';
import { useTheaterPerformances } from '../../hooks/useTheaterPerformances.js';

import './styles.css';
import { useMediaQuery } from '../../hooks/useMediaQuery.js';
import { useScrollSpy } from '../../hooks/useScrollSpy.js';
import CategoryTabs from '../../components/RestaurantMenu/CategoryTabs.js';
import SubCategories from '../../components/RestaurantMenu/SubCategories.js';
import {
  guestFacingError,
  serverMessage,
  soldOutFromError,
  subscriptionBlockedFromError,
} from '../../utils/serverError';
import OrderingPausedScreen, { orderingPausedText } from '../../components/OrderingPausedScreen';

const DEFAULT_IMG =
  'https://res.cloudinary.com/burgerkingnorway/image/upload/v1674745616/Heywaitr/1674745616-images.png';

// Dropoff coordinates for the Wolt quote — only ever the ones Google returned
// for the address the customer picked. A typed-but-unpicked address has none,
// and null lets the backend fall back to Wolt geocoding street/city/post_code,
// which is what happened before coordinates were supported. The device's own
// cached position is deliberately not used: it is where the phone is, which is
// not necessarily where the food goes.
const getDeliveryCoords = (address) => {
  if (Number.isFinite(address?.lat) && Number.isFinite(address?.long)) {
    return { lat: address.lat, long: address.long };
  }
  return null;
};

// No discount code in play. A frozen module-level constant so clearing the code
// never hands the render tree a fresh object identity.
const EMPTY_DISCOUNT = Object.freeze({
  valid: false,
  code: '',
  amount: 0,
  type: null,
  value: 0,
});

// This venue calls its dine-in tile "Table Service". Applied on top of
// utils/orderTypes.js, at both places the tiles are labelled (the fetch and the
// language switch), so that shared module stays identical to master's.
const TABLE_SERVICE_RESTAURANT_ID = '68dce057628356001a007dd2';
const withVenueOrderTypeLabels = (options, restaurantId) =>
  restaurantId === TABLE_SERVICE_RESTAURANT_ID
    ? options.map((option) =>
        option.id === 'dine-in' ? { ...option, label: 'Table Service' } : option,
      )
    : options;

export default function RestaurantMenu() {
  let pathname = window.location.pathname;
  const restaurantId = pathname.split('/')?.[2];
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();

  const currentStep = useMemo(() => {
    const path = location.pathname.split('/').pop();
    const validSteps = ['choose-order', 'menu', 'review', 'payment'];
    return validSteps.includes(path) ? path : 'menu';
  }, [location.pathname]);

  // Context Variables
  const {
    userProfile,
    setOrderFromRestaurantsNotAllowed,
    language,
    setLanguageFromRestaurant,
    clearRestaurantLanguageOverride,
  } = useDataContext();
  const { restaurant, setRestaurant } = useRestaurantContext();
  const orderContext = useOrderContext();
  const {
    progressByProduct,
    fetchMenuProgress,
    fetchWallet,
    vouchers,
    stageVoucher,
    stagedVoucher,
    clearStagedVoucher,
    voucherDiscount = 0,
    refreshAfterOrder,
  } = useStampWallet() || {};

  // Hook Variables
  const isMobile = useMediaQuery('(max-width: 639px)');
  const { lagTime } = useLagTime();
  const {
    orderStatus,
    startPendingOrderPolling,
    startConfirmedOrderPolling,
    checkOrderStatus,
    payConfirm,
    setPayConfirm,
    isProcessingOrderModalOpen,
    setIsProcessingOrderModalOpen,
    cancelOrder,
    isCancelling,
  } = useOrderStatusPolling(userProfile);
  const { restoredData, hasRestoredData, persistOrderData, clearPersistedData } =
    usePreorderPersistence();
  // Theater mode: upcoming orderable performances. Only fetched when the venue
  // has TheaterModeEnabled — an empty list for every other restaurant.
  const {
    performances: theaterPerformances,
    opensDate: theaterOpensDate,
    fetchPerformances: fetchTheaterPerformances,
    hasFetched: hasFetchedTheaterPerformances,
  } = useTheaterPerformances(restaurant?._id, Boolean(restaurant?.TheaterModeEnabled));

  // State Variables
  const [restaurantDescription, setRestaurantDescription] = useState('');
  const [orderItems, setOrderItems] = useState([]);

  /*
   * Stock — INVENTORY-SPEC.md §4.1. Asks the venue whether the basket can be
   * served, debounced, so a guest who wants five of something there are four of
   * finds out at the cart instead of at the payment step. Inert at venues
   * without stock control: the endpoint answers ok and nothing renders.
   */
  // Its own mapper rather than formatOrdersForCheckout: that one is declared
  // near the bottom of this component, and a const is not hoisted, so calling it
  // from up here would throw before the page ever rendered.
  //
  // Menu lines and extras are all the check needs. Attributes that point at
  // another menu item (a combo drawing down its component) are NOT included:
  // the cart holds them as {attributeId, optionId} and only the backend's order
  // builder resolves those to menu ids. Such a line is still caught by the
  // reservation at checkout — this is a courtesy check, not the guard.
  const stockLines = useMemo(
    () =>
      (orderItems || []).map((item) => ({
        _id: item._id,
        qty: item.quantity,
        extras: (item.extras || [])
          .filter((extra) => extra.quantity > 0)
          .map((extra) => ({ _id: extra.id, qty: extra.quantity })),
      })),
    [orderItems],
  );

  // Gated on the venue's own flag: without it the cart would ask about stock at
  // every venue, and almost none of them use it. The flag carries no numbers —
  // the guest never sees a stock level.
  const stockCheck = useStockCheck({
    restaurantId: restaurant?._id,
    lines: stockLines,
    enabled: restaurant?.InventoryEnabled === true,
  });

  // What the venue said about the quantity being added, shown inside the product
  // modal so the guest finds out where the + button is rather than two screens
  // later in the cart.
  const [addStockError, setAddStockError] = useState('');
  const [checkingAdd, setCheckingAdd] = useState(false);

  const [orderType, setOrderType] = useState('dine-in');
  // Did the guest pick the pre-order tile? A theater pre-order rides on the
  // take-away order type (see utils/orderTypes.js), so the order type alone can
  // no longer tell the two apart now that a theater venue offers both. Every
  // rule about TIMING — opening hours, the pickup now/later toggle, the
  // performance picker, the theaterPerformanceId on the payload — keys on this
  // and not on the venue merely HAVING theater mode enabled.
  const [isTheaterPreorder, setIsTheaterPreorder] = useState(false);
  // Which venue the closed-venue landing above has already run for.
  const closedLandingRef = useRef(null);

  // Ticks once a minute so the tiles re-enable on their own when the venue
  // opens. Nothing else re-renders on the clock, so without it a guest who
  // arrives five minutes early sits on greyed tiles until they reload.
  const [openingTick, setOpeningTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setOpeningTick((n) => n + 1), 60000);
    return () => clearInterval(id);
  }, []);

  // Filled from the venue's own flags once it loads (buildOrderTypeOptions).
  // Starting empty rather than with a guessed dine-in/take-away pair keeps the
  // choose-order step from flashing tiles the venue may not offer.
  const [orderTypeOptions, setOrderTypeOptions] = useState([]);
  const [hasBag, setHasBag] = useState(false);
  const [hasDeliveryFee, setHasDeliveryFee] = useState(false);
  const [hasServiceFee, setHasServiceFee] = useState(false);
  const [hasFlatFee, setHasFlatFee] = useState(false);
  const [isWoltDeliveryEnabled, setIsWoltDeliveryEnabled] = useState(false);
  const [comments, setComments] = useState('');
  const [deliveryComment, setDeliveryComment] = useState('');
  const [activeGroup, setActiveGroup] = useState('order-now');
  const [categories, setCategories] = useState([]);
  const [categoriesCopy, setCategoriesCopy] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  // const [selectedCategory, setSelectedCategory] = useState(null);
  // const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  const [tableNumbers, setTableNumbers] = useState([]);
  const [restaurantTips, setRestaurantTips] = useState([]);
  const [woltDelivery, setWoltDelivery] = useState(null);
  // When the quote above arrived and which slot it was asked for — Wolt
  // promises lapse after 60 minutes, and one fetched for another date or time
  // quotes the wrong delivery. See utils/woltDelivery.
  const [woltQuoteMeta, setWoltQuoteMeta] = useState(null);
  // Only the newest /woltDelivery/check may write its answer: a slow response
  // for an address or time the guest has since changed must not overwrite it.
  const woltQuoteSeqRef = useRef(0);
  const [woltDeliveryError, setWoltDeliveryError] = useState(NO_WOLT_ERROR);
  const [inAppLoading, setInAppLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isDeleteCardModalOpen, setIsDeleteCardModalOpen] = useState(false);
  const [deleteCardModalMessage, setDeleteCardModalMessage] = useState('');
  const [deleteCardId, setDeleteCardId] = useState(null);
  // State Variables for ItemSelection
  const [isItemSelectionModalOpen, setIsItemSelectionModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  // The message belongs to the item that is open. Opening another one — or
  // closing the modal — must not carry it along.
  useEffect(() => {
    setAddStockError('');
  }, [selectedItem?._id]);
  const [itemQuantity, setItemQuantity] = useState(0);
  const [selectedExtras, setSelectedExtras] = useState([]);
  const [selectedAttributes, setSelectedAttributes] = useState([]);
  const [isValidationErrorModalOpen, setIsValidationErrorModalOpen] = useState(false);
  const [validationErrorMessage, setValidationErrorMessage] = useState('');
  const [itemComments, setItemComments] = useState('');
  // State Variables for OrderInformation
  const [isValidPhone, setIsValidPhone] = useState(true);
  const [tableNumber, setTableNumber] = useState(null);
  const [tipPercentage, setTipPercentage] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [discountCode, setDiscountCode] = useState('');
  // Guest discount code. `discountApplied.amount` is the SERVER's number from
  // /discountcode/validate — the client never derives it, and only the code
  // STRING is ever sent with the order.
  const [discountApplied, setDiscountApplied] = useState(EMPTY_DISCOUNT);
  const [discountStatus, setDiscountStatus] = useState('idle'); // idle|checking|applied|error|unsupported
  const [discountError, setDiscountError] = useState('');
  // Bumped by the Apply button / Enter so the block validates without waiting
  // out its 400 ms debounce.
  const [discountApplyNonce, setDiscountApplyNonce] = useState(0);
  const [isCheckoutModalOpen, setIsCheckoutModalOpen] = useState(false);
  const [tipAmount, setTipAmount] = useState(0);
  const [pickup, setPickup] = useState('now');
  const [pickupOptions, setPickupOptions] = useState([
    { id: 'now', label: translations[language]?.pickup_now },
    { id: 'later', label: translations[language]?.pickup_later },
  ]);
  const [pickupDate, setPickupDate] = useState(new Date());
  const [pickupTime, setPickupTime] = useState('12:00');
  // The moment the guest is actually ordering FOR. Higher-category windows have
  // to be judged against this, not against "right now" — otherwise a pre-order
  // for Tuesday evening is judged by Sunday's schedule (and vice versa).
  const orderDateTime = useMemo(() => {
    if (pickup !== 'later' || !pickupDate) {
      return new Date();
    }

    const combined = moment(
      `${moment(pickupDate).format('MM/DD/YYYY')} ${pickupTime}`,
      'MM/DD/YYYY HH:mm',
    );

    return combined.isValid() ? combined.toDate() : new Date();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickup, pickupDate, pickupTime]);
  // Theater mode: id of the chosen performance ("YYYY-MM-DD#idx"), or null.
  // Only ever sent with checkout when the restaurant has TheaterModeEnabled.
  const [theaterPerformanceId, setTheaterPerformanceId] = useState(null);
  const [isPickupForTomorrow, setIsPickupForTomorrow] = useState(false);
  const [bagFee, setBagFee] = useState('4');
  const [deliveryFee, setDeliveryFee] = useState('50');
  const [woltDeliveryFee, setWoltDeliveryFee] = useState('');
  const [serviceFee, setServiceFee] = useState('');
  const [flatFee, setFlatFee] = useState('');
  const [roomNumber, setRoomNumber] = useState('');
  // lat/long stay null until the address lookup fills them; see getDeliveryCoords
  const [address, setAddress] = useState({
    street: '',
    city: '',
    zipcode: '',
    lat: null,
    long: null,
  });
  const [capacityFullModal, setCapacityFullModal] = useState({ open: false, nextSlot: null });
  // State Variables for GuestInformation
  const [guestInfo, setGuestInfo] = useState({
    name: '',
    email: '',
  });
  // State Variables for YourItems
  const [editingCartId, setEditingCartId] = useState(null);
  // Loyalty discount percent (active member), lifted up so the footer total matches the cart
  const [loyaltyDiscountPercent, setLoyaltyDiscountPercent] = useState(0);
  // State Variables for Payment
  const [myCards, setMyCards] = useState([]);
  const [tipId, setTipId] = useState(null);
  const [isViewOpeningTimesOpen, setIsViewOpeningTimesOpen] = useState(false);
  const [orderNotAllowed, setOrderNotAllowed] = useState(false);
  const [menuNotAvailable, setMenuNotAvailable] = useState(false);
  // Distinct from menuNotAvailable (which fires when a guest tries to order
  // from a closed category): this one explains an empty page — the venue is
  // open but none of its menus is served at the time being ordered for.
  const [noMenuAvailable, setNoMenuAvailable] = useState(false);
  const [isRushModeModalOpen, setIsRushModeModalOpen] = useState(false);
  const [isPromoPopupModalOpen, setIsPromoPopupModalOpen] = useState(false);

  // Ref Variables
  const yourItemsRef = useRef(null);
  const orderInfoRef = useRef();
  const guestInfoRef = useRef();
  const addressRef = useRef();
  const paymentRef = useRef(null);
  const isRestoredNavigationRef = useRef(false);
  // A scheduled order restored after a failed payment keeps its day and time
  // until the guest changes the order type or the day.
  const restoredScheduleRef = useRef(null);

  const hasHigherCategories = categories.length > 0;
  // Whether a venue organises its menu into higher categories is a property of
  // the venue (categoriesCopy — all of them), not of what happens to be
  // servable at the moment being ordered for (categories — the open ones).
  // Conflating the two is what let a venue whose menus are all closed for the
  // day fall through to the flat "no higher categories" layout and render its
  // entire card as orderable.
  const hasHigherCategoriesConfigured = categoriesCopy.length > 0;
  const noCategoryServable = hasHigherCategoriesConfigured && !hasHigherCategories;
  const pickupTimeInterval = 10;

  // Explain the empty page rather than leaving the guest staring at nothing.
  // Raised once per entry into that state, so dismissing it sticks — but a
  // guest who switches to a pickup time when nothing is served sees it again.
  const noMenuNoticeShownRef = useRef(false);
  useEffect(() => {
    if (!noCategoryServable) {
      noMenuNoticeShownRef.current = false;
      return;
    }

    if (noMenuNoticeShownRef.current) return;

    noMenuNoticeShownRef.current = true;
    setNoMenuAvailable(true);
  }, [noCategoryServable]);

  const groupedMenuItems = useMemo(() => {
    if (!menuItems?.length) return [];

    // The venue does group its menu into higher categories, but none of them is
    // served at the ordered-for time, so there is nothing to show. Without this
    // the flat branch below renders every item the venue has.
    if (noCategoryServable) return [];

    if (!hasHigherCategories) {
      // Group by category only when no higher categories
      return chain(menuItems)
        .groupBy('category')
        .map((items, category) => ({
          category,
          higherCategoryId: null,
          menu: items.sort((a, b) => (a.order || 0) - (b.order || 0)),
        }))
        .value();
    }

    // Build lookup: subCategoryId -> array of parent category _ids
    const subCategoryToParentIds = {};
    categories.forEach((category) => {
      category.TaggedCategories?.forEach((tagged) => {
        if (!subCategoryToParentIds[tagged._id]) {
          subCategoryToParentIds[tagged._id] = [];
        }
        subCategoryToParentIds[tagged._id].push(category._id);
      });
    });

    // Expand menu items to appear under all their parent categories
    const expandedMenuItems = [];
    menuItems.forEach((item) => {
      const parentIds = subCategoryToParentIds[item.categoryid] || [null];
      parentIds.forEach((parentId) => {
        expandedMenuItems.push({
          ...item,
          higherCategoryId: parentId,
        });
      });
    });

    // Group by both category and higherCategoryId
    return chain(expandedMenuItems)
      .groupBy((item) => `${item.higherCategoryId}_${item.category}`)
      .map((items) => ({
        category: items[0]?.category,
        higherCategoryId: items[0]?.higherCategoryId || null,
        menu: items.sort((a, b) => (a.order || 0) - (b.order || 0)),
      }))
      .sortBy((group) => {
        const parentCategory = categories.find((cat) => cat._id === group.higherCategoryId);
        return parentCategory?.Order ?? 999;
      })
      .value()
      .filter((item) => item.higherCategoryId);
  }, [menuItems, categories, hasHigherCategories, noCategoryServable]);

  // Filter menu items based on order type & hideondinein & hideontakeout
  const filteredGroupedMenuItems = useMemo(() => {
    return groupedMenuItems
      .filter((group) => {
        const subCategory = subCategories.find((sub) => sub.Category === group.category);
        if (!subCategory) return true; // Keep if no matching subcategory found

        if (subCategory.IsHidden) return false;
        if (orderType === 'dine-in' && subCategory.HideOnDineIn) return false;
        if (orderType === 'take-away' && subCategory.HideOnTakeOut) return false;

        return true;
      })
      .map((category) => ({
        ...category,
        menu: category.menu.filter((item) => {
          if (orderType === 'dine-in' && item.hideondinein) return false;
          if (orderType === 'take-away' && item.hideontakeout) return false;
          return true;
        }),
      }))
      .filter((category) => category.menu.length > 0)
      .sort((a, b) => {
        // Sort by parent category index first
        const parentIndexA = categories.findIndex((cat) => cat._id === a.higherCategoryId);
        const parentIndexB = categories.findIndex((cat) => cat._id === b.higherCategoryId);

        if (parentIndexA !== parentIndexB) {
          return (
            (parentIndexA === -1 ? 999 : parentIndexA) - (parentIndexB === -1 ? 999 : parentIndexB)
          );
        }

        // Then sort by subcategory order
        const subCatA = subCategories.find((sub) => sub.Category === a.category);
        const subCatB = subCategories.find((sub) => sub.Category === b.category);

        return (subCatA?.Order ?? 999) - (subCatB?.Order ?? 999);
      });
  }, [groupedMenuItems, orderType, categories, subCategories]);

  const scrollSpyData = useMemo(() => {
    return categories.map((cat) => ({
      ...cat,
      sections: filteredGroupedMenuItems.filter((g) => g.higherCategoryId === cat._id),
    }));
  }, [categories, filteredGroupedMenuItems]);

  const {
    activeCategory,
    activeSubCategory,
    scrollTo,
    subNavRef,
    setActiveCategory,
    setActiveSubCategory,
  } = useScrollSpy(scrollSpyData, {
    offset: 180,
    getCategoryId: (cat) => cat._id,
    getSections: (cat) => cat.sections,
    getSectionId: (section) => `${section.higherCategoryId}_${section.category}`,
    // Open on the first higher category that is actually servable today. A menu
    // whose first category is e.g. lunch would otherwise land the guest on a
    // closed category on days lunch is not served.
    isCategorySelectable: (cat) => cat.isAvailable !== false,
  });

  const selectedCategory =
    categoriesCopy?.find((category) => category._id === activeCategory) || null;

  // Filter subcategories based on their category
  const filteredSubCategories = useMemo(() => {
    if (!subCategories.length) return [];

    // If no higher categories, show all sub-categories that have menu items
    if (!hasHigherCategories) {
      return subCategories.filter((subCategory) =>
        filteredGroupedMenuItems.find((group) => group.category === subCategory.Category),
      );
    }

    // When higher categories exist
    if (!selectedCategory) return [];

    return subCategories.filter(
      (subCategory) =>
        selectedCategory.TaggedCategories.some((cat) => cat._id === subCategory._id) &&
        filteredGroupedMenuItems.find((group) => group.category === subCategory.Category),
    );
  }, [selectedCategory, subCategories, filteredGroupedMenuItems, hasHigherCategories]);

  const currentSubCategories = useMemo(() => {
    const currentCategory = scrollSpyData.find((cat) => cat._id === activeCategory);
    return currentCategory?.sections || [];
  }, [scrollSpyData, activeCategory]);

  // console.log('currentSubCategories', currentSubCategories);
  // console.log('scrollSpyData', scrollSpyData);

  // Get current sub-categories for active category
  const handleCategorySelect = (category) => {
    const _filteredGroupedMenuItems = filteredGroupedMenuItems.filter(
      (g) => g.higherCategoryId === category._id,
    );

    if (_filteredGroupedMenuItems?.length === 0) {
      setIsErrorModalOpen(true);
      setErrorMessage(translations[language]?.menu_grid_items_unavailable);
      return;
    }

    const firstSection = filteredGroupedMenuItems.find((g) => g.higherCategoryId === category._id);
    if (firstSection) {
      scrollTo(`${firstSection.higherCategoryId}_${firstSection.category}`, 180);
    }
  };

  const handleSubCategorySelect = (subCategory) => {
    const id = `${subCategory.higherCategoryId}_${subCategory.category || subCategory.Category || subCategory.name}`;
    scrollTo(id, 180);
  };

  const getRestaurantDescriptionTranslation = (item, language) => {
    return Object.keys(item?.Translation || {}).length > 0
      ? item.Translation?.[language]?.Description
      : item?.description || '';
  };

  const getCategoriesTranslation = (data, _language, restaurantLanguage) => {
    return data.map((category) => {
      let language = _language;

      if (
        Object.keys(category?.Translation || {}).length === 1 &&
        Object.keys(category?.Translation || {}).includes(restaurantLanguage)
      ) {
        language = restaurantLanguage;
      }

      if (!Object.keys(category?.Translation || {}).includes(language)) {
        language = Object.keys(category?.Translation || {})[0];
      }

      return {
        ...category,
        Category:
          Object.keys(category?.Translation || {}).length > 0
            ? category.Translation?.[language]?.Category || ''
            : category?.Category || '',
        Description:
          Object.keys(category?.Translation || {}).length > 0
            ? category.Translation?.[language]?.Description || ''
            : category?.Description || '',
      };
    });
  };

  const getSubCategoriesTranslation = (data, _language, restaurantLanguage) => {
    return data.map((subCategory) => {
      let language = _language;

      if (
        Object.keys(subCategory?.Translation || {}).length === 1 &&
        Object.keys(subCategory?.Translation || {}).includes(restaurantLanguage)
      ) {
        language = restaurantLanguage;
      }

      if (!Object.keys(subCategory?.Translation || {}).includes(language)) {
        language = Object.keys(subCategory?.Translation || {})[0];
      }

      return {
        ...subCategory,
        Category:
          Object.keys(subCategory?.Translation || {}).length > 0
            ? subCategory.Translation?.[language]?.Category || ''
            : subCategory?.Category || '',
      };
    });
  };

  const getMenuTranslation = (data, data2, _language, restaurantLanguage) => {
    const translatedMenuItems = data.map((item) => {
      let language = _language;

      if (
        Object.keys(item?.Translation || {}).length === 1 &&
        Object.keys(item?.Translation || {}).includes(restaurantLanguage)
      ) {
        language = restaurantLanguage;
      }

      if (!Object.keys(item?.Translation || {}).includes(language)) {
        language = Object.keys(item?.Translation || {})[0];
      }

      return {
        ...item,
        name:
          Object.keys(item?.Translation || {}).length > 0
            ? item.Translation?.[language]?.Name || ''
            : item?.name || '',
        description:
          Object.keys(item?.Translation || {}).length > 0
            ? item.Translation?.[language]?.Description || ''
            : item?.description || '',
      };
    });

    const translatedSubCategories = data2.map((subCategory) => {
      let language = _language;

      if (
        Object.keys(subCategory?.Translation || {}).length === 1 &&
        Object.keys(subCategory?.Translation || {}).includes(restaurantLanguage)
      ) {
        language = restaurantLanguage;
      }

      if (!Object.keys(subCategory?.Translation || {}).includes(language)) {
        language = Object.keys(subCategory?.Translation || {})[0];
      }

      return {
        ...subCategory,
        Category:
          Object.keys(subCategory?.Translation || {}).length > 0
            ? subCategory.Translation?.[language]?.Category || ''
            : subCategory?.Category || '',
      };
    });

    return translatedMenuItems?.map((item, index, array) => {
      let language = _language;

      const itemFind = translatedSubCategories.find(
        (subCategory) => subCategory._id === item.categoryid,
      );

      if (itemFind) {
        const attributes =
          item?.attributes.map((attribute) => {
            const attributeFind = translatedSubCategories.find(
              (subCategory) => subCategory._id === attribute._id,
            );
            const options =
              attribute?.options.map((option) => {
                let result = { ...option };

                const optionFind = translatedSubCategories.find(
                  (subCategory) => subCategory._id === (option?.maincategoryid || null),
                );
                const optionFind2 = translatedSubCategories.find(
                  (subCategory) => subCategory._id === (option?.CategoryId || null),
                );
                const optionFind3 = array.find(
                  (menuItem) => menuItem._id === (option?._id || null),
                );

                if (optionFind) {
                  result = {
                    ...result,
                    maincategory: optionFind.Category || '',
                  };
                }

                if (optionFind2) {
                  result = {
                    ...result,
                    Category: optionFind2.Category || '',
                  };
                }

                if (optionFind3) {
                  result = {
                    ...result,
                    name: optionFind3.name || '',
                    Name: optionFind3.name || '',
                  };
                }

                if (result.minlimit) {
                  return {
                    ...result,
                    minlimit: Number(result.minlimit) || null,
                  };
                }

                return result;
              }) || [];

            if (attributeFind) {
              return {
                ...attribute,
                name: attributeFind.Category || '',
                options: attribute?.options?.length > 0 ? options : [],
              };
            }

            return { ...attribute, options: attribute?.options?.length > 0 ? options : [] };
          }) || [];

        return {
          ...item,
          category: itemFind.Category || '',
          categorydesc: itemFind.Description || '',
          attributes: item?.attributes?.length > 0 ? attributes : [],
        };
      }

      return item;
    });
  };

  const getOrderItemsTranslation = (data, _language, restaurantLanguage) => {
    return (
      data?.map((orderItem) => {
        let language = _language;

        if (
          Object.keys(orderItem?.Translation || {}).length === 1 &&
          Object.keys(orderItem?.Translation || {}).includes(restaurantLanguage)
        ) {
          language = restaurantLanguage;
        }

        if (!Object.keys(orderItem?.Translation || {}).includes(language)) {
          language = Object.keys(orderItem?.Translation || {})[0];
        }

        return {
          ...orderItem,
          name:
            Object.keys(orderItem?.Translation || {}).length > 0
              ? orderItem.Translation?.[language]?.Name || ''
              : orderItem?.name || '',
          description:
            Object.keys(orderItem?.Translation || {}).length > 0
              ? orderItem.Translation?.[language]?.Description || ''
              : orderItem?.description || '',
        };
      }) || []
    );
  };

  useEffect(() => {
    if (typeof window === 'undefined') return;

    if (isBlockedRestaurant(restaurantId)) {
      navigate('/', { replace: true });
      return;
    }

    let language = 'en';

    const fetchData = async () => {
      try {
        setLoading(true);
        const [
          restaurantData,
          categoriesData,
          subCategoriesData,
          menuData,
          tableNumberData,
          tipsData,
        ] = await Promise.all([
          axios.post(
            `${configData.SERVER_URL}/mobile/restaurant/fetchspecific?id=${restaurantId}`,
            {
              id: restaurantId,
            },
            { headers: { Authorization: `Bearer ${userProfile?.token}` } },
          ),
          axios.get(`${configData.SERVER_URL}/highercategory/fetch?restaurant=${restaurantId}`),
          axios.get(`${configData.SERVER_URL}/menu/category/fetch?restaurant=${restaurantId}`),
          axios.get(
            `${configData.SERVER_URL}/menu/fetch?restaurant=${restaurantId}&category=All`,
            // Attach auth only when logged in, so guests send the exact same
            // request as before. The token lets the backend return HeyWaitr+
            // member pricing for subscribers; for everyone else the response is
            // unchanged.
            userProfile?.token
              ? { headers: { Authorization: `Bearer ${userProfile.token}` } }
              : undefined,
          ),
          axios.get(`${configData.SERVER_URL}/tableno/mobile/fetch?restaurant=${restaurantId}`, {
            id: restaurantId,
          }),
          axios.get(`${configData.SERVER_URL}/tips/mobile/fetch?restaurant=${restaurantId}`, {
            Authorization: `Bearer ${userProfile?.token}`,
          }),
        ]);

        // Process restaurant data
        const processedRestaurant = {
          ...restaurantData.data,
          availability: restaurantData.data.AvailabilityOriginal,
        };

        setLanguageFromRestaurant(processedRestaurant);

        if (processedRestaurant?.currency.code) {
          language = renderCurrencyLanguage(processedRestaurant?.currency.code);
        }

        if (window.localStorage.getItem('user_lang')) {
          language = window.localStorage.getItem('user_lang');
        }

        const processedSubCategories = getSubCategoriesTranslation(
          subCategoriesData.data,
          language,
          renderCurrencyLanguage(processedRestaurant?.currency.code),
        );

        // Get menu items
        const menuItems = getMenuTranslation(
          menuData.data.data,
          subCategoriesData.data,
          language,
          renderCurrencyLanguage(processedRestaurant?.currency.code),
        );

        // Process existing higher categories with availability
        const processedCategoriesData = categoriesData.data
          ?.filter((category) => !category.IsAddOn)
          .map((category) => ({
            ...category,
            availability: category.Availability,
            isAvailable: isCategoryAvailable(category),
          }));
        const processedCategories = getCategoriesTranslation(
          processedCategoriesData,
          language,
          renderCurrencyLanguage(processedRestaurant?.currency.code),
        );

        // Find higher categories that exist in the menu items but not in the fetched categories
        const existingCategoryIds = new Set(
          processedCategories.flatMap(
            (cat) => cat.TaggedCategories?.map((tagged) => tagged._id) || [],
          ),
        );

        // Group items by their categoryid to find potential missing higher categories
        const categoryGroups = {};
        menuItems.forEach((item) => {
          if (item.categoryid && !existingCategoryIds.has(item.categoryid)) {
            if (!categoryGroups[item.category]) {
              categoryGroups[item.category] = {
                name: item.category,
                items: [],
              };
            }
            categoryGroups[item.category].items.push(item);
          }
        });

        // Process menu items
        const sortedMenu = sortBy(menuItems, ['category']);

        // Sort sub-categories
        const sortedSubCategories = sortBy(processedSubCategories, ['Order'], ['asc']);

        // Combine existing and new higher categories
        const allCategories = [...processedCategories];

        // Translate restaurant's description
        const restaurantDescription = processedRestaurant?.Translation
          ? processedRestaurant.Translation?.[language]?.Description
          : processedRestaurant?.description || '';

        // Higher categories the venue serves on the day being ordered for. The
        // dine-in and take-away branches this replaces were identical, and a
        // venue with neither flag set fell through to `return category` —
        // keeping every category, on or off.
        const filteredCategories = allCategories.filter((category) =>
          isHigherCategoryServedOn(category, orderDateTime),
        );

        setRestaurant(processedRestaurant);
        setRestaurantDescription(restaurantDescription);
        setCategories(filteredCategories);
        setCategoriesCopy(allCategories);
        setActiveCategory(filteredCategories?.[0] || null);
        setMenuItems(sortedMenu);
        setSubCategories(sortedSubCategories);

        // Set default order type options based on restaurant settings.
        // A theater venue is an ordinary venue that ALSO sells pre-orders for
        // its programme: the normal tiles are built from its own flags exactly
        // as anywhere else, and the pre-order tile is added beside them. It used
        // to REPLACE them, which left a guest in the foyer at 17:00 unable to
        // buy a drink for now — the only path on offer held the order back
        // until half an hour before curtain.
        const orderTypeOptions = withVenueOrderTypeLabels(
          buildOrderTypeOptions(processedRestaurant, language),
          restaurantId,
        );

        const lastOrderId = localStorage.getItem('hw_lastorderid');

        if (!isRestoredNavigationRef.current) {
          if (orderTypeOptions.length === 1 && orderTypeOptions[0]?.id === TICKETS_TILE) {
            // A venue that sells ONLY tickets has no menu to land on — its one
            // tile is the ticket page, which lives outside this flow. The
            // generic "one tile, skip the picker" rule below sent the guest to
            // an empty menu instead. Replace, so Back returns to the list.
            navigate(`/events/${restaurantId}`, { replace: true });
            return;
          }
          // Ordering paused: land on the menu, not on order-type tiles the
          // guest cannot use.
          if (processedRestaurant.orderingPaused) {
            navigateReplace('menu');
          } else if (orderTypeOptions?.length > 1) {
            if (lastOrderId) {
              localStorage.removeItem('hw_lastorderid');
              navigateReplace('menu');
            } else {
              navigateReplace('choose-order');
            }
          } else {
            navigateReplace('menu');
          }
        }

        setOrderTypeOptions(orderTypeOptions || []);

        // Set default pickup options that will depend on allowAdvanceOrder
        const pickupOptions = [{ id: 'now', label: translations[language]?.pickup_now }];
        if (processedRestaurant.allowAdvanceOrder) {
          pickupOptions.push({ id: 'later', label: translations[language]?.pickup_later });
        }

        setPickupOptions(pickupOptions || []);

        // Set default order type based on restaurant settings. A theater venue
        // no longer forces the pre-order flow here — it has real tiles now, and
        // the guest picks one on the choose-order step. Landing on the venue's
        // ordinary default keeps a theater with dine-in behaving like any other
        // dine-in venue until the guest says otherwise.
        if (isRestoredNavigationRef.current) {
          // Back from a failed payment: keep the order the restore effect put back.
        } else if (orderTypeOptions.length === 1 && orderTypeOptions[0]?.id === THEATER_TILE) {
          // A theater that sells nothing but its programme — which is what every
          // theater was before this branch, and what a venue that has not been
          // reconfigured still is.
          //
          // Keyed on the built tiles rather than on the venue flags, because
          // this has to fire in exactly the case the block above skips the
          // choose-order step: one tile, and it is the pre-order. That step is
          // the ONLY place isTheaterPreorder is set, so the flow has to be
          // established here or the guest silently lands on an order-now — sent
          // with theaterOrderNow, judged against the venue's opening hours, and
          // refused at exactly the venue whose whole purpose is selling while
          // shut. A theater that offers any immediate way to order still shows
          // the chooser, and its guest still picks.
          setOrderType('take-away');
          setIsTheaterPreorder(true);
          setHasBag(false);
          setHasDeliveryFee(false);
        } else if (processedRestaurant.isdinein) {
          setOrderType('dine-in');
          setHasBag(false);
          setHasDeliveryFee(false);
        } else if (processedRestaurant.istakeout) {
          setOrderType('take-away');
          // Not at a theater: there the performance IS the schedule, for both
          // flows, and the pickup toggle is never shown. Leaving it on 'later'
          // made every theater order claim isAdvanceOrder with a default date
          // of today 12:00 — which also filtered the menu against noon.
          if (processedRestaurant.allowAdvanceOrder && !processedRestaurant.TheaterModeEnabled) {
            setPickup('later');
          }

          if (!processedRestaurant.HasDeliveryFee) {
            setHasDeliveryFee(false);
          }

          if (!processedRestaurant.BagTakeAway) {
            setHasBag(false);
          }
        } else if (orderTypeOptions.length === 1 && orderTypeOptions[0]?.id === 'delivery') {
          // A venue that only delivers. Its one tile skips the choose-order
          // step, so select it here as the app does (hey_waitr soleOrderMode):
          // left on the 'dine-in' default, the guest reached a dine-in checkout
          // at a venue that has none. Set up as picking the tile would be — a
          // delivery always carries a bag.
          setOrderType('delivery');
          setHasBag(true);
          if (processedRestaurant.allowAdvanceOrder) {
            setPickup(
              defaultPickupMode({
                allowAdvanceOrder: true,
                openNow:
                  Boolean(processedRestaurant.isOpen) &&
                  getRestaurantStatus(processedRestaurant, true, moment()),
              }),
            );
          }
        }

        // Set default fee values from restaurant
        if (processedRestaurant.BagTakeAwayPrice) {
          setBagFee(processedRestaurant.BagTakeAwayPrice);
        }

        if (processedRestaurant.DeliveryFee) {
          setDeliveryFee(processedRestaurant.DeliveryFee);
        }

        if (processedRestaurant.serviceFee) {
          setHasServiceFee(true);
          setServiceFee(processedRestaurant.serviceFee);
        } else {
          setHasServiceFee(false);
        }

        if (processedRestaurant.flatFee) {
          setHasFlatFee(true);
          setFlatFee(processedRestaurant.flatFee);
        } else {
          setHasFlatFee(false);
        }

        const tableNumbers = tableNumberData.data.map((table) => {
          return {
            label: table.number,
            value: table.number,
            ...table,
          };
        });

        setTableNumbers(tableNumbers);
        setRestaurantTips(tipsData.data);
      } catch (error) {
        console.log('error', error);
        setError(error);
        console.error('Error fetching restaurant data:', error);
      } finally {
        setLoading(false);
      }
    };

    if (restaurantId) {
      fetchData();
    }

    // // Always set the step upon page load
    // navigateReplace('menu');

    localStorage.setItem('return_url', pathname);

    //check last order
    orderContext.checkPendingOrder();

    // Cleanup function to clear restaurant language override when leaving
    return () => {
      clearRestaurantLanguageOverride();
    };
  }, [restaurantId]);

  // Load cart from localStorage
  useEffect(() => {
    const savedCart = localStorage.getItem('hw_cart');
    if (savedCart) {
      try {
        const parsedCart = JSON.parse(savedCart);
        if (restaurantId === parsedCart.restaurantId) {
          setOrderItems(parsedCart.items || []);
          setOrderType(parsedCart.orderType || 'dine-in');
          setComments(parsedCart.comments || '');
          setDeliveryComment(parsedCart.deliveryComment || '');
        }
      } catch (e) {
        console.error('Error parsing saved cart:', e);
      }
    }
  }, [restaurantId]);

  // §9.2/§9.3 — load stamp progress pills + redeemable vouchers for this
  // restaurant on mount. Fail-safe-to-empty: the context swallows errors and
  // anonymous/no-token resolves to {} / [], so the menu renders unchanged.
  useEffect(() => {
    if (!restaurantId) return;
    fetchMenuProgress?.(restaurantId);
    fetchWallet?.(restaurantId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurantId]);

  // One-click "Redeem now" from the Wallet. Phase 1: once the menu is loaded,
  // replace the cart with just the reward product and stage the voucher (free).
  const stampRedeemHandledRef = useRef(false);
  const stampRedeemNavRef = useRef(false); // pending nav: the reward product id
  useEffect(() => {
    const intent = location.state?.stampRedeem;
    if (!intent || stampRedeemHandledRef.current) return;
    if (!menuItems?.length) return; // wait for the menu to load
    stampRedeemHandledRef.current = true;
    // Clear the router state so back/refresh doesn't re-trigger the redeem.
    try {
      window.history.replaceState({}, document.title);
    } catch (e) {
      /* no-op */
    }

    const product = menuItems.find((m) => String(m._id) === String(intent.productId));
    if (!product) return; // reward not on this menu → just land on the menu

    const price = Number(product.price ?? product.Price ?? 0);
    setOrderItems([
      {
        ...product,
        cartId: `${product._id}-${Date.now()}`,
        quantity: 1,
        extras: [],
        selectedAttributes: [],
        comments: '',
        totalPrice: price,
      },
    ]);

    const v = (Array.isArray(vouchers) ? vouchers : []).find(
      (x) => String(x?.Code || x?.code || '') === String(intent.code || ''),
    ) || { Code: intent.code };
    if (typeof stageVoucher === 'function') {
      try {
        stageVoucher(v, intent.productId);
      } catch (e) {
        /* no-op */
      }
    }
    // Defer the jump to checkout until the cart actually holds the product, so the
    // empty-cart guard (which bounces /review → /menu) doesn't fire mid-flush.
    stampRedeemNavRef.current = String(product._id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, menuItems, vouchers]);

  // Phase 2: once the reward product is in the cart, jump straight to checkout.
  useEffect(() => {
    if (!stampRedeemNavRef.current) return;
    const has = (orderItems || []).some(
      (it) => String(it._id) === String(stampRedeemNavRef.current),
    );
    if (has) {
      stampRedeemNavRef.current = false;
      try {
        navigateToStep('review');
      } catch (e) {
        /* no-op */
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderItems]);

  useEffect(() => {
    if (!language) return;

    const translateOptions = (options, translationKeys) =>
      options?.map((item) => ({
        ...item,
        label: translationKeys[item.id] || item.label,
      })) || [];

    // Tile labels come from one place (utils/orderTypes.js) so the language
    // switch cannot drift from the initial render — the bug that used to
    // translate the theater tile back to a plain "Afhent".
    setOrderTypeOptions((current) =>
      withVenueOrderTypeLabels(translateOrderTypeOptions(current, language, restaurant), restaurantId),
    );

    setPickupOptions(
      translateOptions(pickupOptions, {
        now: translations[language]?.pickup_now,
        later: translations[language]?.pickup_later,
      }),
    );

    const translatedRestaurantDescription = getRestaurantDescriptionTranslation(
      restaurant,
      language,
    );
    const translatedCategories = getCategoriesTranslation(
      categories,
      language,
      renderCurrencyLanguage(restaurant?.currency.code),
    );
    const translatedCategoriesCopy = getCategoriesTranslation(
      categoriesCopy,
      language,
      renderCurrencyLanguage(restaurant?.currency.code),
    );
    const translatedMenuItems = getMenuTranslation(
      menuItems,
      subCategories,
      language,
      renderCurrencyLanguage(restaurant?.currency.code),
    );
    const translatedSubCategories = getSubCategoriesTranslation(
      subCategories,
      language,
      renderCurrencyLanguage(restaurant?.currency.code),
    );
    const translatedOrderItems = getOrderItemsTranslation(
      orderItems,
      language,
      renderCurrencyLanguage(restaurant?.currency.code),
    );

    setRestaurantDescription(translatedRestaurantDescription);
    setCategories(translatedCategories);
    setCategoriesCopy(translatedCategoriesCopy);
    setMenuItems(translatedMenuItems);
    setSubCategories(translatedSubCategories);
    setOrderItems(translatedOrderItems);
  }, [restaurant, language]);

  // Save cart to localStorage
  useEffect(() => {
    if (restaurant) {
      const cartData = {
        items: orderItems,
        orderType,
        hasBag,
        hasDeliveryFee,
        comments,
        deliveryComment,
        restaurantId,
        timestamp: new Date().toISOString(),
      };

      localStorage.setItem('hw_cart', JSON.stringify(cartData));
    }
  }, [
    orderItems,
    orderType,
    hasBag,
    hasDeliveryFee,
    comments,
    deliveryComment,
    restaurant,
    restaurantId,
  ]);

  // Handle order type change - filter incompatible items
  useEffect(() => {
    if (orderItems.length > 0) {
      const filteredItems = orderItems.filter((item) => {
        // Filter out items that don't work with current order type
        if (orderType === 'dine-in' && item.hideontakeout) {
          return false;
        }
        if (orderType === 'take-away' && item.hideondinein) {
          return false;
        }
        return true;
      });

      if (filteredItems.length !== orderItems.length) {
        setOrderItems(filteredItems);
      }
    }

    // `categories` is derived from `categoriesCopy`, but the two live in
    // separate state and the fetch sets them in separate, unbatched calls
    // (React 17). Between those two commits this effect fires with
    // `categoriesCopy` still empty and would reset the categories the fetch
    // just set — leaving one render where the venue has higher categories
    // configured but none servable, which pops the "no menu is served right
    // now" notice on every load. An empty copy has nothing to derive from, so
    // skip: `categories` is set from the same source a moment later anyway.
    if ((orderType === 'dine-in' || orderType === 'take-away') && categoriesCopy.length > 0) {
      const filteredCategories = categoriesCopy.filter((category) =>
        isHigherCategoryServedOn(category, orderDateTime),
      );

      setCategories(filteredCategories);

      const hiddenCategories = categoriesCopy.filter(
        (category) => !isHigherCategoryServedOn(category, orderDateTime),
      );

      // Pick the first available category as fallback
      const fallback = filteredCategories?.[0] || null;

      // If the previously selected category is hidden, use fallback
      setActiveCategory((prev) => {
        const isPrevHidden = prev && hiddenCategories.some((h) => h._id === prev._id);
        return isPrevHidden ? fallback : prev;
      });
    }
  }, [orderType, categoriesCopy, orderDateTime]);

  useEffect(() => {
    // Extract phone number from user profile if available
    if (userProfile?.profile?.mobilenumber) {
      let number = userProfile.profile.mobilenumber;
      if (
        number?.toString?.()?.substring?.(0, 2) == '45' ||
        number?.toString?.()?.substring?.(0, 3) == '+45'
      ) {
        setPhoneNumber(userProfile.profile.mobilenumber);
      } else {
        setPhoneNumber('45' + number);
      }
    }
  }, [userProfile]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    if (!restaurant?.RushMode) return;

    // Check URL params to see if we should hide rush mode
    const params = new URLSearchParams(window.location.search);
    if (params.get('hideRushMode')) return;

    setIsRushModeModalOpen(true);
  }, [restaurant?.RushMode]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    if (!restaurant?.PromoPopUpEnabled) return;

    setIsPromoPopupModalOpen(true);
  }, [restaurant?.PromoPopUpEnabled]);

  // Check for existing order on component mount
  useEffect(() => {
    const checkExistingOrder = async () => {
      // Scoped to THIS venue — another restaurant's order used to be restored
      // here, popping its confirmation over this checkout. See utils/pendingOrder.
      // The id off the PATHNAME, not the fetched restaurant: `restaurant` is
      // null until the fetch resolves, and isPendingOrderForVenue fails open on
      // an unknown id — so on the first render of every fresh load the foreign
      // order was adopted anyway, and nothing undid it once the real restaurant
      // arrived. useOrderStatusPolling reads the same value for the same reason.
      const order = readPendingOrderForVenue(
        localStorage.getItem('hw_pending_orderv2'),
        restaurantId,
      );
      if (order) {
        setPayConfirm(order);

        // Check if we need to show the processing modal
        if (!order.status) {
          setIsProcessingOrderModalOpen(true);
          navigateToStep('menu');
        }

        // You can also check the current status immediately
        const currentStatus = await checkOrderStatus(order._id);
        if (currentStatus) {
          // Handle status update
          if (currentStatus.type === 'order_accepted') {
            // keep the status payload — it carries woltDeliveryAccept (tracking url)
            setPayConfirm({ ...order, status: 'confirmed', order: currentStatus });

            // if (order?.status === 'confirmed') {
            //   setIsProcessingOrderModalOpen(true);
            // }
          } else if (currentStatus.type === 'order_cancelled') {
            localStorage.removeItem('hw_pending_orderv2');
            setPayConfirm({});
            setIsProcessingOrderModalOpen(false);
          }
        } else {
          localStorage.removeItem('hw_pending_orderv2');
          setPayConfirm({});
          setIsProcessingOrderModalOpen(false);
        }
      }
    };

    checkExistingOrder();
  }, [restaurant, checkOrderStatus]);

  useEffect(() => {
    return () => {
      // Cleanup on unmount
      localStorage.removeItem('hw_cart');
      // The polling cleanup is handled in the hook itself
    };
  }, []);

  // // Handle browser back/forward navigation
  // useEffect(() => {
  //   const handlePopState = (event) => {
  //     const hasActiveOrder = orderItems.length > 0 && currentStep !== 'choose-order';

  //     if (hasActiveOrder && currentStep === 'menu') {
  //       // If user is trying to leave the menu with items in cart
  //       event.preventDefault();
  //       setShowBackNavigationModal(true);
  //       // Push the current state back to prevent navigation
  //       window.history.pushState(null, '', location.pathname);
  //     }
  //   };

  //   // Listen for browser navigation
  //   window.addEventListener('popstate', handlePopState);

  //   return () => {
  //     window.removeEventListener('popstate', handlePopState);
  //   };
  // }, [orderItems.length, currentStep, location.pathname]);

  // Apply restored data when available
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const params = new URLSearchParams(window.location.search);
    const isPersistData = params.get('persistData');

    if (isPersistData && hasRestoredData && restoredData) {
      isRestoredNavigationRef.current = true;

      // A Wolt quote only comes back while Wolt still honours it. A delivery
      // without one goes back to review, where Next fetches a fresh quote —
      // never to payment, where it would be charged a fee nobody quoted.
      const restoredQuoteFresh = isWoltQuoteFresh(
        restoredData.woltDelivery,
        restoredData.woltQuoteMeta?.fetchedAt,
      );
      const restoredStep =
        restoredData.orderType === 'delivery' &&
        !restoredQuoteFresh &&
        restoredData.currentStep === 'payment'
          ? 'review'
          : restoredData.currentStep || 'menu';

      // Apply all restored values to state
      setOrderType(restoredData.orderType || 'dine-in');
      setPickup(restoredData.pickup || 'now');
      setTimeout(() => navigateReplace(restoredStep), 100);
      if (restoredData.pickupDate) setPickupDate(new Date(restoredData.pickupDate));
      if (restoredData.pickupTime) setPickupTime(restoredData.pickupTime);
      if (restoredData.pickup === 'later' && restoredData.pickupDate) {
        restoredScheduleRef.current = {
          orderType: restoredData.orderType || 'dine-in',
          pickupDate: new Date(restoredData.pickupDate),
        };
      }
      if (restoredData.theaterPerformanceId)
        setTheaterPerformanceId(restoredData.theaterPerformanceId);
      // Older persisted carts predate the flag; a stored performance id is
      // proof enough that the guest was pre-ordering.
      setIsTheaterPreorder(
        restoredData.isTheaterPreorder !== undefined
          ? Boolean(restoredData.isTheaterPreorder)
          : Boolean(restoredData.theaterPerformanceId),
      );
      if (restoredData.phoneNumber) handlePhoneNumberChange(restoredData.phoneNumber);
      if (restoredData.tableNumber) handleTableNumberChange(restoredData.tableNumber);
      if (restoredData.comments) handleCommentsChange(restoredData.comments);
      if (restoredData.deliveryComment) setDeliveryComment(restoredData.deliveryComment);
      if (restoredData.address) setAddress(restoredData.address);
      if (restoredData.orderType === 'delivery' && restoredQuoteFresh) {
        setWoltDelivery(restoredData.woltDelivery);
        setWoltDeliveryFee(woltQuoteFee(restoredData.woltDelivery));
        setWoltQuoteMeta(restoredData.woltQuoteMeta);
      }
      if (restoredData.tipId) setTipId(restoredData.tipId);
      if (restoredData.tipPercentage) handleTipPercentageChange(restoredData.tipPercentage);
      if (restoredData.orderItems?.length) setOrderItems(restoredData.orderItems);
      // Only the code string was persisted. Putting it back feeds it straight
      // into the lookup effect, which re-validates it against the restored cart
      // — the stored amount is never trusted, because there never was one. Data
      // persisted before this feature simply has no discountCode key, so an
      // in-flight redirect across the deploy restores exactly as it does today.
      if (restoredData.discountCode) {
        setDiscountCode(restoredData.discountCode);
        setDiscountStatus('checking');
      }
      if (restoredData.hasBag !== undefined) handleBagToggle(restoredData.hasBag);
      // Set, not toggled: handleDeliveryFeeToggle flips whatever is current, so
      // restoring `true` onto a fee that was already on turned it off.
      if (restoredData.hasDeliveryFee !== undefined)
        setHasDeliveryFee(Boolean(restoredData.hasDeliveryFee));
      if (restoredData.roomNumber) handleRoomNumberChange(restoredData.roomNumber);
      if (restoredData.guestInfo) {
        setGuestInfo(
          restoredData?.guestInfo || {
            name: '',
            email: '',
          },
        );
      }
      if (restoredData.guestId) {
        orderContext.setGuestId(restoredData.guestId || '');
        orderContext.setIsGuest(true);
        localStorage.setItem('guest-uui', restoredData.guestId);
      }

      // Clear after applying
      clearPersistedData();
    }
  }, [hasRestoredData, restoredData, clearPersistedData]);

  // Reset selectedSubCategory when selectedCategory changes
  useEffect(() => {
    if (filteredSubCategories.length > 0) {
      const firstSubCategory =
        filteredSubCategories[0].category ||
        filteredSubCategories[0].Category ||
        filteredSubCategories[0].name;

      setActiveSubCategory(firstSubCategory);
    } else {
      setActiveSubCategory(null);
    }
  }, [selectedCategory, filteredSubCategories]);

  useEffect(() => {
    if (selectedCategory && !isRestoredNavigationRef.current) {
      if (!selectedCategory.isAvailable) {
        if (restaurant.istakeout && !restaurant.isdinein) {
          setOrderType('take-away');
          if (!restaurant.allowAdvanceOrder) {
            return;
          }
          setPickup('later');
        }
      }

      if (
        !restaurant.isOpen &&
        restaurant.istakeout &&
        !restaurant.isdinein &&
        restaurant.allowAdvanceOrder
      ) {
        setOrderType('take-away');
        setPickup('later');
      }
    }
  }, [selectedCategory]);

  useEffect(() => {
    if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
      try {
        window.scrollTo(0, 0);
      } catch (err) {
        window.scrollTo(0, 0);
      }
    }
  }, [currentStep]);

  const navigateToStep = (step) => {
    navigate(`/restaurants/${restaurantId}/${step}`, { replace: false });
  };

  const navigateReplace = (step) => {
    navigate(`/restaurants/${restaurantId}/${step}`, { replace: true });
  };

  // Once an order is placed, Back can land the customer on a stale review/payment
  // step because those pages are still in browser history. Always redirect, but
  // only alert on a real Back/Forward press — placing an order empties the cart
  // and routes here itself, and orderItems is empty on every mount until the
  // saved cart rehydrates, so the first pass stays silent too.
  const hasNavigatedRef = useRef(false);
  useEffect(() => {
    const isBackNavigation = navigationType === 'POP' && hasNavigatedRef.current;
    hasNavigatedRef.current = true;

    if ((currentStep === 'review' || currentStep === 'payment') && orderItems.length === 0) {
      navigateReplace('menu');
      if (isBackNavigation) {
        alert(`${translations[language]?.restaurant_menu_empty_cart}!`);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, orderItems.length, navigationType]);

  // A venue whose ordering is paused (its heywaitr subscription is blocked):
  // the menu stays browsable, but every way into an order stops here with the
  // backend's own wording. Checked before the other gates so the guest is told
  // the real reason, not "closed" or nothing at all.
  const showOrderingPaused = useCallback(
    (message) => {
      setErrorMessage(orderingPausedText(language, message || restaurant?.orderingPausedMessage));
      setIsErrorModalOpen(true);
    },
    [restaurant, language],
  );

  // Item selection modal functions
  const handleItemSelectionModalOpen = (item, cartItemId = null) => {
    if (restaurant.orderingPaused) {
      showOrderingPaused();
      return;
    }

    if (!restaurant.isMenuBuyable) {
      return;
    }

    if (!venueSellsOnline(restaurant)) {
      return;
    }

    if (toggleOrderIfNotAllowed()) {
      setOrderNotAllowed(true);
      return;
    }

    if (checkIfCategoryIsDisabled()) {
      setMenuNotAvailable(true);
      return;
    }

    if (isUserAllowedToOrder) {
      // If a specific cartItemId is provided, we're editing an existing cart item
      if (cartItemId) {
        const existingItem = orderItems.find((orderItem) => orderItem.cartId === cartItemId);
        const menuItem = menuItems.find((menuItem) => menuItem._id === item._id);

        if (existingItem && menuItem) {
          setSelectedItem({ ...menuItem, higherCategoryId: selectedCategory?._id || null });
          setItemQuantity(existingItem.quantity);
          setSelectedExtras(existingItem.extras || []);
          setSelectedAttributes(existingItem.selectedAttributes || []);
          setItemComments(existingItem.comments || '');
          setIsItemSelectionModalOpen(true);
          setEditingCartId(cartItemId);
          return;
        }
      }

      // Initialize with defaults for a new item
      setSelectedItem({ ...item, higherCategoryId: selectedCategory?._id || null });
      setItemQuantity(1);
      setSelectedExtras([]);
      setSelectedAttributes(handleUpdateAttributesFromDefaultValue(item) || []);
      setItemComments('');
      setIsItemSelectionModalOpen(true);
      setEditingCartId(null);
    } else {
      orderContext.setLoginModal(true);
      orderContext.setShouldShowReview(true);
    }
  };

  const handleUpdateAttributesFromDefaultValue = (item) => {
    if (!item?.attributes?.length) return [];

    const initialAttributes = [];

    item.attributes.forEach((attribute) => {
      // Skip the attribute entirely if belongsto is true
      if (attribute.belongsto) return;

      if (!attribute.isMultiple || !attribute.options?.length) return;

      const options = [];

      attribute.options.forEach((option) => {
        const rawDefault = option.defaultValue;
        const defaultVal =
          rawDefault !== null && rawDefault !== undefined && rawDefault !== ''
            ? parseInt(rawDefault, 10)
            : 0;

        if (!isNaN(defaultVal) && defaultVal > 0) {
          options.push({
            id: option._id,
            name: option?.name || option?.Name || 'Unknown',
            additionalPrice: option?.additionalPrice || 0,
            price: option?.price || option?.Price || 0,
            quantity: defaultVal,
            isMultiple: attribute?.isMultiple || false,
            isconnectedto: option?.isconnectedto || null,
          });
        }
      });

      if (options.length > 0) {
        initialAttributes.push({
          id: attribute._id,
          attributeId: attribute._id,
          options,
          isMultiple: attribute?.isMultiple || false,
        });
      }
    });

    return initialAttributes.length > 0 ? initialAttributes : [];
  };

  // Check if items have the same configuration
  const haveSameConfiguration = (item1, item2) => {
    // Check comments
    if (item1.comments !== item2.comments) return false;

    // Check extras
    const extras1 = item1.extras || [];
    const extras2 = item2.extras || [];
    if (extras1.length !== extras2.length) return false;

    // Compare each extra
    for (const extra1 of extras1) {
      const matchingExtra = extras2.find(
        (e) => e.id === extra1.id && e.quantity === extra1.quantity,
      );
      if (!matchingExtra) return false;
    }

    // Check attributes
    const attrs1 = item1.selectedAttributes || [];
    const attrs2 = item2.selectedAttributes || [];
    if (attrs1.length !== attrs2.length) return false;

    // Compare each attribute and its options
    for (const attr1 of attrs1) {
      const matchingAttr = attrs2.find((a) => a.attributeId === attr1.attributeId);
      if (!matchingAttr) return false;

      // Compare connectedFromId (for connected attributes)
      const connectedFromId1 = attr1?.connectedFromId?.toString() || '';
      const connectedFromId2 = matchingAttr?.connectedFromId?.toString() || '';
      if (connectedFromId1 !== connectedFromId2) return false;

      const options1 = attr1?.options || [];
      const options2 = matchingAttr?.options || [];
      if (options1?.length !== options2?.length) return false;

      // Compare each option (including quantity)
      for (const opt1 of options1) {
        const matchingOpt = options2?.find(
          (o) => o.id === opt1?.id && (o?.quantity || 1) === (opt1?.quantity || 1),
        );
        if (!matchingOpt) return false;
      }
    }

    return true;
  };

  const validateRequiredAttributes = () => {
    // Skip validation if there's no item selected
    if (!selectedItem) return true;

    // Check each option with minlimit
    const optionsBelowMinLimit = [];
    let totalQuantity = 0;

    // Check minlimit for all attributes
    (selectedItem.attributes || []).forEach((attribute) => {
      // Skip validation for attributes that belong to parent options
      if (attribute?.belongsto) return;

      attribute?.options?.forEach((sourceOption) => {
        // Find the selected quantity for this option
        const attrSelection = selectedAttributes?.find(
          (attr) => attr.attributeId?.toString() === attribute._id?.toString(),
        );

        const selectedOption = attrSelection?.options?.find(
          (opt) => opt.id?.toString() === sourceOption._id?.toString(),
        );

        const selectedQuantity = selectedOption?.quantity || 0;
        totalQuantity += selectedQuantity;

        // Validate: quantity must meet minlimit
        if (sourceOption?.minlimit) {
          if (selectedQuantity < sourceOption.minlimit) {
            optionsBelowMinLimit.push({ ...sourceOption, quantity: selectedQuantity });
          }
        }
      });
    });

    const availableAttribute = selectedItem.attributes?.[0];

    if (
      availableAttribute?.minNumberAllowedTotalItem &&
      totalQuantity < Number(availableAttribute?.minNumberAllowedTotalItem)
    ) {
      setValidationErrorMessage(
        `${translations[language]?.total || 'Total'} ${
          translations[language]?.minimum || 'Minimum'
        }: ${availableAttribute.minNumberAllowedTotalItem} | ${
          translations[language]?.total || 'Total'
        } ${translations[language]?.selected || 'Selected'}: ${totalQuantity || 0}`,
      );

      return false;
    }

    if (optionsBelowMinLimit?.length > 0) {
      const optionNames = optionsBelowMinLimit
        .map(
          (opt) =>
            `${opt.name} (${translations[language]?.minimum || 'Minimum'}: ${opt.minlimit} | ${
              translations[language]?.selected || 'Selected'
            }: ${opt.quantity || 0})`,
        )
        .join('\n');

      if (optionNames) {
        setValidationErrorMessage(
          `${translations[language]?.restaurant_menu_minimum_quantity_message}:\n${optionNames}`,
        );

        return false;
      }

      return false;
    }

    // Get all required attributes
    const requiredAttributes =
      selectedItem.attributes?.filter((attr) => attr.attributesRequired) || [];

    // If no required attributes, validation passes
    if (requiredAttributes.length === 0) return true;

    // Check each required attribute to see if it has a selection
    for (const requiredAttr of requiredAttributes) {
      // Find if this attribute has a selection in selectedAttributes
      const attrSelection = selectedAttributes
        ?.filter((attr) =>
          requiredAttr?.isMultiple
            ? attr?.options.some((option) => option.quantity && option.quantity > 0)
            : attr,
        )
        ?.find((attr) => attr.attributeId?.toString() === requiredAttr._id?.toString());

      // If no selection or empty options array, validation fails
      if (
        !requiredAttr.belongsto &&
        (!attrSelection || !attrSelection?.options || attrSelection?.options.length === 0)
      ) {
        setValidationErrorMessage(
          `${translations[language]?.restaurant_menu_select_option_message} "${requiredAttr.name}"`,
        );
        return false;
      }

      if (!requiredAttr.belongsto) {
        const connectedAttributes = [];

        if (attrSelection) {
          attrSelection?.options.forEach((item) => {
            if (item?.isconnectedto && item?.isconnectedto.length > 0) {
              item?.isconnectedto.forEach((item2) => {
                const itemFind = requiredAttributes?.find((item3) => item3._id === item2);

                if (itemFind) {
                  connectedAttributes.push(itemFind);
                }
              });
            }
          });
        }

        for (const requiredConnectedAttri of connectedAttributes) {
          const connectedAttrSelection = selectedAttributes?.find(
            (attr) => attr.attributeId?.toString() === requiredConnectedAttri._id?.toString(),
          );

          if (
            !connectedAttrSelection ||
            !connectedAttrSelection?.options ||
            connectedAttrSelection?.options.length === 0
          ) {
            setValidationErrorMessage(
              `${translations[language]?.restaurant_menu_select_option_message} "${requiredConnectedAttri.name}"`,
            );
            return false;
          }
        }
      }
    }

    return true;
  };

  const handleAddToOrder = async () => {
    // Validate required attributes first
    if (!validateRequiredAttributes()) {
      setIsValidationErrorModalOpen(true);
      return;
    }

    /*
     * Stock, at the moment of adding. The cart check further down catches this
     * too, but only once the guest has moved on — asking for ten of something
     * there are five of should be answered right here, with the quantity
     * stepper still in front of them.
     *
     * Checked against what the basket would hold AFTER this add: the same item
     * may already be on another line, and the venue's limit is on the item, not
     * on one line of it. While editing a line, that line's current quantity is
     * excluded — it is being replaced, not added to.
     */
    const alreadyInCart = (orderItems || [])
      .filter((item) => item._id === selectedItem?._id && item.cartId !== editingCartId)
      .reduce((sum, item) => sum + (item.quantity || 0), 0);

    const wanted = alreadyInCart + itemQuantity;
    if (restaurant?.InventoryEnabled === true && restaurant?._id && selectedItem?._id && wanted > 0) {
      setCheckingAdd(true);
      const verdict = await checkStockForAdd({
        restaurantId: restaurant._id,
        item: selectedItem,
        quantity: wanted,
        extras: selectedExtras,
      });
      setCheckingAdd(false);
      if (verdict.message) {
        setAddStockError(verdict.message);
        return;
      }
    }
    setAddStockError('');

    const selectedAttributesArray = selectedAttributes.map((selectedAttr) => {
      const attribute = selectedItem?.attributes?.find(
        (attr) => attr._id?.toString() === selectedAttr?.attributeId?.toString(),
      );

      return {
        id: selectedAttr?.attributeId,
        attributeId: selectedAttr?.attributeId,
        connectedFromId: selectedAttr?.connectedFromId || null,
        attributeName: attribute?.name || translations[language]?.unknown || 'Unknown',
        options: selectedAttr?.options,
        isMultiple: attribute?.isMultiple || false,
      };
    });

    if (editingCartId) {
      // Create the updated item with the same cartId
      const updatedItem = {
        ...selectedItem,
        cartId: editingCartId,
        quantity: itemQuantity,
        extras: selectedExtras,
        selectedAttributes: selectedAttributesArray,
        comments: itemComments,
        totalPrice: calculateModalTotalPrice(),
      };

      // Check if there's already an item with the same configuration (excluding the one being edited)
      const existingItemIndex = orderItems.findIndex(
        (item) =>
          item.cartId !== editingCartId && // Skip the item being edited
          item?._id === selectedItem?._id &&
          haveSameConfiguration(item, updatedItem),
      );

      if (existingItemIndex !== -1) {
        // Remove the item being edited and update the quantity of the existing item
        setOrderItems((prev) =>
          prev
            .filter((item) => item.cartId !== editingCartId) // Remove the item being edited
            .map((item, index) =>
              index === existingItemIndex
                ? {
                    ...item,
                    quantity: item.quantity + itemQuantity,
                    totalPrice: calculateItemTotal(item, item.quantity + itemQuantity),
                  }
                : item,
            ),
        );
      } else {
        // Replace the old item with the updated one
        setOrderItems((prev) =>
          prev.map((item) => (item.cartId === editingCartId ? updatedItem : item)),
        );
      }
    } else {
      const tempItem = {
        _id: selectedItem._id,
        extras: selectedExtras,
        selectedAttributes: selectedAttributesArray,
        comments: itemComments,
      };

      // Check if there's already an item with the same configuration
      const existingItemIndex = orderItems.findIndex(
        (item) => item?._id === selectedItem?._id && haveSameConfiguration(item, tempItem),
      );

      if (existingItemIndex !== -1) {
        // Update the quantity of the existing item instead of adding a new one
        setOrderItems((prev) =>
          prev.map((item, index) =>
            index === existingItemIndex
              ? {
                  ...item,
                  quantity: item.quantity + itemQuantity,
                  totalPrice: calculateItemTotal(item, item.quantity + itemQuantity),
                }
              : item,
          ),
        );
      } else {
        // Add as a new item
        const itemToAdd = {
          ...selectedItem,
          cartId: `${selectedItem._id}-${Date.now()}`,
          quantity: itemQuantity,
          extras: selectedExtras,
          selectedAttributes: selectedAttributesArray,
          comments: itemComments,
          totalPrice: calculateModalTotalPrice(),
        };

        setOrderItems((prev) => [...prev, itemToAdd]);
      }
    }

    // Reset the editing state and close the modal
    setEditingCartId(null);
    setIsItemSelectionModalOpen(false);
  };

  const editCartItem = (item) => {
    handleItemSelectionModalOpen(item, item.cartId);
  };

  const handleRadioAttributeChange = (attributeId, selectedOption, connectedFromId) => {
    setSelectedAttributes((prev) => {
      // Find the current selection for this attribute (if any)
      const existingAttribute = prev.find((attr) => attr.attributeId === attributeId);
      const previousOptionId = existingAttribute?.options?.[0]?.id?.toString() || '';

      // Remove any existing selection for this attribute
      let filtered = prev.filter((attr) => attr.attributeId !== attributeId);

      // If there was a previous selection and it's different from the new one,
      // remove any attributes connected to the old option
      if (previousOptionId && previousOptionId !== (selectedOption?.id?.toString() || '')) {
        filtered = filtered.filter((attr) => {
          // Check if this attribute has any options connected to the previous option
          const hasConnectionToPreviousOption = attr.options?.some(
            (opt) => opt?.isconnectedto?.toString() === previousOptionId,
          );
          const connectedFromIdStr = attr.connectedFromId?.toString();

          // Keep the attribute only if it doesn't have connections to the previous option
          return connectedFromIdStr !== previousOptionId && !hasConnectionToPreviousOption;
        });
      }

      // If no option is selected, just return the filtered array
      if (!selectedOption) {
        return filtered;
      }

      // Add the new selection
      return [
        ...filtered,
        {
          id: attributeId,
          attributeId: attributeId,
          connectedFromId,
          options: [selectedOption],
        },
      ];
    });
  };

  const handleMultipleAttributeChange = (attributeId, isSelected, option) => {
    setSelectedAttributes((prev) => {
      // Find if we already have this attribute in our array
      const existingAttributeIndex = prev.findIndex((attr) => attr.attributeId === attributeId);

      if (existingAttributeIndex === -1 && isSelected) {
        // If attribute doesn't exist and option is selected, create a new entry
        return [
          ...prev,
          {
            id: attributeId,
            attributeId,
            options: [option],
          },
        ];
      } else if (existingAttributeIndex !== -1) {
        // Attribute exists, update its options
        const updatedAttributes = [...prev];
        const attribute = selectedItem?.attributes?.find(
          (attr) => attr._id?.toString() === attributeId?.toString(),
        );

        if (isSelected) {
          // Check if the attribute allows multiple selections
          if (attribute?.isMultiple) {
            // Add option to existing options
            updatedAttributes[existingAttributeIndex] = {
              ...updatedAttributes[existingAttributeIndex],
              options: [...updatedAttributes[existingAttributeIndex].options, option],
            };
          } else {
            // Replace existing options for non-multiple attributes
            updatedAttributes[existingAttributeIndex] = {
              ...updatedAttributes[existingAttributeIndex],
              options: [option],
            };
          }
        } else {
          // Remove option from options
          updatedAttributes[existingAttributeIndex] = {
            ...updatedAttributes[existingAttributeIndex],
            options: updatedAttributes[existingAttributeIndex].options.filter(
              (opt) => opt.id !== option.id,
            ),
          };

          // If no options left, remove the attribute entry
          if (updatedAttributes[existingAttributeIndex].options.length === 0) {
            updatedAttributes.splice(existingAttributeIndex, 1);
          }
        }

        return updatedAttributes;
      }

      return prev;
    });
  };

  const handleMultipleCounterChange = (attributeId, itemId, newCounts) => {
    setSelectedAttributes((prev) => {
      // Find if we already have this attribute in our array
      const existingAttributeIndex = prev.findIndex((attr) => attr.attributeId === attributeId);

      // Find the attribute from the selected item
      const attribute = selectedItem?.attributes?.find(
        (attr) => attr._id?.toString() === attributeId?.toString(),
      );

      if (existingAttributeIndex === -1) {
        // If attribute doesn't exist and at least one option has a count > 0, create a new entry
        const options = [];

        // Process all counts and create options for those with count > 0
        Object.entries(newCounts).forEach(([optionId, count]) => {
          if (count > 0) {
            const option = attribute?.options?.find(
              (opt) => opt._id?.toString() === optionId?.toString(),
            );

            if (option) {
              options.push({
                id: option._id,
                name: option?.name || translations[language]?.unknown || 'Unknown',
                additionalPrice: option?.additionalPrice || 0,
                price: option?.price || 0,
                quantity: count,
                isMultiple: attribute?.isMultiple || false,
                isconnectedto: option?.isconnectedto || null,
              });
            }
          }
        });

        // Only add the attribute if there are options with counts
        if (options.length > 0) {
          return [
            ...prev,
            {
              id: attributeId,
              attributeId,
              options,
              isMultiple: attribute?.isMultiple || false,
            },
          ];
        }
      } else {
        // Attribute exists, update its options
        const updatedAttributes = [...prev];

        // Create new options array based on the new counts
        const options = [];
        Object.entries(newCounts).forEach(([optionId, count]) => {
          // Only include options with count > 0
          if (count > 0) {
            const option = attribute?.options?.find(
              (opt) => opt._id?.toString() === optionId?.toString(),
            );

            if (option) {
              options.push({
                id: option._id,
                name: option?.name || translations[language]?.unknown || 'Unknown',
                additionalPrice: option?.additionalPrice || 0,
                price: option?.price || 0,
                quantity: count,
                isMultiple: attribute?.isMultiple || false,
                isconnectedto: option?.isconnectedto || null,
              });
            }
          }
        });

        // Calculate total quantity for the current attribute from newCounts
        const currentAttributeQuantity = newCounts[itemId];

        // If current attribute's quantity is 0, remove the attribute entry
        if (currentAttributeQuantity === 0) {
          const currentItemIdStr = itemId?.toString();

          // Remove attributes that have options connected to the removed option
          const filteredAttributes = updatedAttributes.filter((attr) => {
            // Check if this attribute has any options connected to the removed option
            const hasConnectionToRemovedItem = attr.options?.some(
              (opt) => opt?.isconnectedto?.toString() === currentItemIdStr,
            );
            const connectedFromIdStr = attr.connectedFromId?.toString();

            // Keep the attribute only if it doesn't have connections to the removed item
            return currentItemIdStr !== connectedFromIdStr && !hasConnectionToRemovedItem;
          });

          // Update the current attribute with remaining options
          if (options.length > 0) {
            filteredAttributes[existingAttributeIndex] = {
              ...updatedAttributes[existingAttributeIndex],
              options,
            };
            return filteredAttributes;
          } else {
            // If no options left, also remove the current attribute
            return filteredAttributes.filter(
              (attr) => attr.attributeId?.toString() !== attributeId?.toString(),
            );
          }
        } else {
          // Otherwise update with the new options
          updatedAttributes[existingAttributeIndex] = {
            ...updatedAttributes[existingAttributeIndex],
            options,
          };
        }

        return updatedAttributes;
      }

      return prev;
    });
  };

  const handleExtrasChange = (newCounts) => {
    // Convert the counts object to an array of extras
    const extrasArray = Object.entries(newCounts)
      .filter(([_, count]) => count > 0)
      .map(([id, count]) => {
        const extra = selectedItem?.extras?.find((extra) => extra._id === id);
        return {
          id,
          name: extra?.name || translations[language]?.unknown || 'Unknown',
          price: extra?.price || 0,
          quantity: count,
        };
      });

    setSelectedExtras(extrasArray);
  };

  const handleRemoveFromOrder = (cartId) => {
    setOrderItems((prev) => prev.filter((item) => item.cartId !== cartId));

    // If cart is empty, reset additional options
    if (orderItems.length <= 1) {
      setHasBag(false);
      setHasDeliveryFee(false);
      setComments('');
      setDeliveryComment('');
      navigateToStep('menu');
    }
  };

  const handleUpdateItemQuantity = (cartId, newQuantity) => {
    if (newQuantity <= 0) {
      handleRemoveFromOrder(cartId);
      return;
    }

    setOrderItems((prev) =>
      prev.map((item) =>
        item.cartId === cartId
          ? { ...item, quantity: newQuantity, totalPrice: calculateItemTotal(item, newQuantity) }
          : item,
      ),
    );
  };

  const calculateModalTotalPrice = () => {
    let total = selectedItem?.price || 0;

    // Add extras
    selectedExtras?.forEach((extra) => {
      total += Number(extra?.price || 0) * Number(extra?.quantity || 1);
    });

    // Add attributes price
    selectedAttributes?.forEach((attribute) => {
      if (attribute?.isMultiple) {
        attribute?.options?.forEach((option) => {
          if (option && (option?.Price || option?.additionalPrice)) {
            if (attribute?.connectedFromId) {
              // Find the parent option this attribute is connected from
              const parentOption = selectedAttributes
                ?.flatMap((attr) => attr?.options || [])
                ?.find((opt) => opt?.id === attribute?.connectedFromId);

              total += Number(option?.Price || 0) * Number(parentOption?.quantity || 1);
            } else {
              total += Number(option?.additionalPrice || 0) * Number(option?.quantity || 1);
            }
          }
        });

        return;
      }

      attribute?.options?.forEach((option) => {
        if (option && (option?.Price || option?.additionalPrice)) {
          if (attribute?.connectedFromId) {
            // Find the parent option this attribute is connected from
            const parentOption = selectedAttributes
              ?.flatMap((attr) => attr?.options || [])
              ?.find((opt) => opt?.id === attribute?.connectedFromId);

            total += Number(option?.Price || 0) * Number(parentOption?.quantity || 1);
          } else {
            total += Number(option?.additionalPrice || 0);
          }
        }
      });
    });

    return total * itemQuantity;
  };

  const calculateItemTotal = (item, quantity) => {
    let price = item?.price || 0;

    // Add extras
    item?.extras?.forEach((extra) => {
      price += Number(extra?.price || 0) * Number(extra?.quantity || 1);
    });

    // Add selected attributes
    item?.selectedAttributes?.forEach((attr) => {
      if (attr?.isMultiple) {
        attr?.options?.forEach((option) => {
          if (option && (option?.Price || option?.additionalPrice)) {
            if (attr?.connectedFromId) {
              // Find the parent option this attribute is connected from
              const parentOption = item?.selectedAttributes
                ?.flatMap((a) => a?.options || [])
                ?.find((opt) => opt?.id === attr?.connectedFromId);

              price += Number(option?.Price || 0) * Number(parentOption?.quantity || 1);
            } else {
              price += Number(option?.additionalPrice || 0) * Number(option?.quantity || 1);
            }
          }
        });

        return;
      }

      attr?.options?.forEach((option) => {
        if (option && (option?.Price || option?.additionalPrice)) {
          if (attr?.connectedFromId) {
            // Find the parent option this attribute is connected from
            const parentOption = item?.selectedAttributes
              ?.flatMap((a) => a?.options || [])
              ?.find((opt) => opt?.id === attr?.connectedFromId);

            price += Number(option?.Price || 0) * Number(parentOption?.quantity || 1);
          } else {
            price += Number(option?.additionalPrice || 0);
          }
        }
      });
    });

    return price * quantity;
  };

  // The bag the basket shows and totals — the SAME rule the payload charges
  // (utils/orderTypes.bagTakeAwayPriceFor). hasBag alone is only the toggle: it
  // stays on after the guest switches from delivery to pickup, and at a venue
  // that sells no bags the basket then showed 4 kr the guest was never charged.
  // An empty basket carries no bag (it read "Total DKK 4.00" before a single item).
  const bagCharge = orderItems?.length
    ? bagTakeAwayPriceFor({
        restaurant,
        orderType,
        isDineIn: orderType === 'dine-in',
        hasBag,
      })
    : 0;

  const calculateTotal = (
    excludeTip = false,
    excludeBagFee = false,
    excludeDeliveryFee = false,
    excludeWoltDeliveryFee = false,
    excludeDiscountCode = false,
  ) => {
    let total = orderItems.reduce(
      (sum, item) => sum + (item.totalPrice || calculateItemTotal(item, item.quantity)),
      0,
    );

    // Guest discount code — off the ITEM SUBTOTAL, before tip / bag / delivery,
    // so the tip percentage and the service fee are both derived from the
    // reduced amount, mirroring how the backend prices the order.
    // discountCodeAmount is SERVER-COMPUTED; it is never derived here.
    //
    // excludeDiscountCode exists for exactly one caller: the struck-through
    // "before" figure in YourItems. Because the code also lowers the base for
    // the tip and the service fee, that figure cannot be recovered by adding the
    // amount back on. Every existing call site is unaffected — the flag defaults
    // to false, so the discount is always applied unless asked otherwise.
    if (!excludeDiscountCode) {
      total = Math.max(
        0,
        total - (discountApplied?.valid ? Number(discountApplied.amount) || 0 : 0),
      );
    }

    // Add tip if not excluded
    if (!excludeTip && (tipPercentage?.percentage || 0) > 0) {
      total += parseFloat(tipPercentage?.percentage || 0) * total;
    }

    // Add take-away bag fee if applicable
    if (
      !excludeBagFee &&
      (orderType === 'take-away' || orderType === 'delivery') &&
      bagCharge > 0
    ) {
      total += bagCharge;
    }

    // Add delivery fee if applicable
    if (!excludeDeliveryFee && orderType === 'take-away' && hasDeliveryFee && deliveryFee) {
      total += parseFloat(deliveryFee);
    }

    // Add wolt delivery fee if applicable
    if (
      !excludeWoltDeliveryFee &&
      orderType === 'delivery' &&
      restaurant?.EnableWoltDelivery &&
      woltDeliveryFee
    ) {
      total += parseFloat(woltDeliveryFee);
    }

    return total;
  };

  const grandTotal = (excludeDiscountCode = false) => {
    let total = calculateTotal(false, false, false, false, excludeDiscountCode);

    if (hasServiceFee && serviceFee && !hasFlatFee && !flatFee) {
      total += total * parseFloat(serviceFee / 100);
    } else if (hasServiceFee && serviceFee && hasFlatFee && flatFee) {
      total += flatFee && total ? flatFee : 0;
      total += total * parseFloat(serviceFee / 100);
    } else {
      total += flatFee && total ? flatFee : 0;
    }

    return total;
  };

  const calculatedServiceFee = () => {
    let total = calculateTotal();

    if (hasServiceFee && serviceFee && !hasFlatFee && !flatFee) {
      let calculatedServiceFee = total * parseFloat(serviceFee / 100);

      return calculatedServiceFee;
    } else if (hasServiceFee && serviceFee && hasFlatFee && flatFee) {
      total += flatFee && total ? flatFee : 0;
      let calculatedServiceFee = total * parseFloat(serviceFee / 100);

      return calculatedServiceFee;
    }

    return null;
  };

  const handleBagToggle = (hasBag) => {
    setHasBag(hasBag);
  };

  const handleDeliveryFeeToggle = useCallback(() => {
    setHasDeliveryFee(!hasDeliveryFee);
  }, [hasDeliveryFee]);

  const handleWoltDeliveryFeeToggle = useCallback(() => {
    if (!isWoltDeliveryEnabled) {
      setWoltDelivery(null);
    }

    setIsWoltDeliveryEnabled(!isWoltDeliveryEnabled);
  }, [isWoltDeliveryEnabled]);

  // The order value the venue's delivery minimum is measured against: the items
  // only, net of the discount code, the Regulars percentage and a staged reward
  // voucher (woltMinOrderValue). Tip, bag and delivery fees are all left out —
  // and the Wolt fee necessarily so, since it is what the quote returns. This
  // is the exact number posted as `totalAmount` below, so the notice the guest
  // reads and the backend's own gate can never disagree.
  const woltMinOrder = useMemo(
    () =>
      checkWoltMinOrder(
        restaurant,
        woltMinOrderValue({
          itemsTotal: calculateTotal(true, true, true, true),
          loyaltyPercent: loyaltyDiscountPercent,
          voucherDiscount: Number(voucherDiscount) || 0,
        }),
        language,
      ),
    [
      restaurant,
      orderItems,
      discountApplied.amount,
      loyaltyDiscountPercent,
      voucherDiscount,
      language,
    ],
  );

  // Drops the quote the guest holds, and any answer still in flight for it.
  // Called whenever what it was quoted for — the address, the date, the order
  // type — changes, so Next and Pay can never go ahead on a quote for
  // something else.
  const invalidateWoltQuote = () => {
    woltQuoteSeqRef.current += 1;
    setWoltDelivery(null);
    setWoltDeliveryFee('');
    setWoltQuoteMeta(null);
    setWoltDeliveryError(NO_WOLT_ERROR);
  };

  // A stuck error from an earlier cart (a minimum-order refusal, say) must not
  // outlive the cart it was about.
  useEffect(() => {
    setWoltDeliveryError(NO_WOLT_ERROR);
  }, [orderItems]);

  // Fetches a quote and stores it. Resolves to { quote, error }: `quote` is null
  // when there is none to be had, `error` the guest-facing reason when the
  // backend refused, and `stale` is set when a newer request (or an edit)
  // superseded this one — its answer is thrown away.
  const fetchWoltQuote = async (_deliveryTime = null, _pickup = null) => {
    let deliveryTime = pickupTime;
    let pickupType = pickup;

    // A new request makes every earlier one stale, and the quote held until now
    // stands for whatever the guest had before this change.
    const seq = ++woltQuoteSeqRef.current;
    setWoltDelivery(null);
    setWoltDeliveryFee('');
    setWoltQuoteMeta(null);
    setWoltDeliveryError(NO_WOLT_ERROR);

    // Below the minimum the backend refuses to quote, so asking would spend a
    // round trip to open an error modal over a form the guest is still filling
    // in. The notice above the address says why, and handleNextStep blocks on
    // the same check at the gate. Venues with no minimum configured never
    // reach this.
    if (!woltMinOrder.met) {
      return { quote: null, error: null };
    }

    if (_deliveryTime) {
      deliveryTime = _deliveryTime;
    }

    if (_pickup) {
      pickupType = _pickup;
    }

    if (pickupType === 'now') {
      deliveryTime = getEarliestTimeForPickupNow();
    }

    if (!(restaurant?.EnableWoltDelivery && address?.street && address?.city && address?.zipcode)) {
      return { quote: null, error: null };
    }

    const currentDate = moment(pickupDate).format('MM/DD/YYYY');
    const currentTime = moment(deliveryTime, 'HH:mm').format('h:mm A');
    const formattedDateTime = moment(currentDate + ' ' + currentTime).format();

    setInAppLoading(true);

    try {
      const coords = getDeliveryCoords(address);

      let woltPayload = {
        restaurantId,
        street: address.street,
        city: address.city,
        post_code: address.zipcode,
        // ASAP orders must not carry a pickup_date — the backend would book
        // the courier as a scheduled delivery instead of dispatching now
        pickup_date: pickupType === 'now' ? null : formattedDateTime,
        // optional dropoff coordinates — the backend forwards them to Wolt and
        // falls back to geocoding street/city/post_code when they are null
        lat: coords?.lat ?? null,
        long: coords?.long ?? null,
        // what the venue's minimum order value is checked against, server-side
        totalAmount: woltMinOrder.orderValue,
      };

      const response = await axios({
        method: 'POST',
        url: `${configData.SERVER_URL}/woltDelivery/check`,
        data: woltPayload,
        headers: {
          Authorization: `Bearer ${userProfile?.token || ''}`,
          'Content-Type': 'application/json',
        },
      });

      setInAppLoading(false);
      if (seq !== woltQuoteSeqRef.current) return { quote: null, error: null, stale: true };

      // Keep the quote EXACTLY as Wolt returned it — it is sent back as the
      // shipment when booking, and dividing price.amount to major units here
      // is what gave Wolt decimal amounts in the delivery payload.
      // customer_price (server-computed, may be venue-subsidized) is what we
      // display and charge.
      const quote = response.data;
      setWoltDelivery(quote);
      setWoltDeliveryFee(woltQuoteFee(quote));
      setWoltQuoteMeta({
        fetchedAt: Date.now(),
        slot: woltQuoteSlot(pickupType, pickupDate, deliveryTime),
      });
      return { quote, error: null };
    } catch (error) {
      setInAppLoading(false);
      if (seq !== woltQuoteSeqRef.current) return { quote: null, error: null, stale: true };

      // A minimum-order rejection carries its own fields, and the server's
      // `message` for it is English with a hardcoded "kr" — so say it in the
      // guest's language instead. This is the guard's backstop: it fires when
      // the cart moved between the check above and the request, or when the
      // venue's minimum reached us through this 400 and not through
      // fetchspecific. Every other refusal is told by its status, never by the
      // server's English message — see woltQuoteErrorMessage.
      const errorMessage =
        woltMinOrderRejection(error.response?.data, restaurant, language) ||
        woltClosedRejection(error, translations[language]) ||
        woltQuoteErrorMessage(error, translations[language]) ||
        "We couldn't get a delivery price right now. Please try again in a moment.";
      setWoltDeliveryError({ error: true, message: errorMessage });
      return { quote: null, error: errorMessage };
    }
  };

  // The address form and the delivery-time controls re-quote through this: it
  // opens the error modal itself, because nothing else is waiting on the answer.
  const handleCheckWoltDeliveryBlur = async (e, _deliveryTime = null, _pickup = null) => {
    const { quote, error } = await fetchWoltQuote(_deliveryTime, _pickup);

    if (error) {
      setIsErrorModalOpen(true);
      setErrorMessage(error);
    } else if (quote) {
      setIsErrorModalOpen(false);
      setErrorMessage('');
    }

    //returned so callers can act on the fresh quote without waiting for the
    //state update
    return quote;
  };

  // For the gates — Next on review and Pay on payment — which must stop when
  // there is no quote, and say why.
  const requireWoltQuote = async () => {
    const { quote, error, stale } = await fetchWoltQuote();

    if (!quote?.id && !stale) {
      setIsErrorModalOpen(true);
      setErrorMessage(
        error ||
          translations[language]?.delivery_quote_missing ||
          'We could not fetch a delivery price for your address. Please check the address and try again.',
      );
    }

    return quote?.id ? quote : null;
  };

  const handleOrderTypeChange = (type) => {
    if (toggleOrderIfNotAllowed(type)) {
      setOrderNotAllowed(true);
      return;
    }

    if (type === 'delivery') {
      handleBagToggle(true);
    } else {
      // A quote fetched while on delivery would otherwise ride along on a
      // pickup order and book a courier nobody asked for.
      invalidateWoltQuote();
    }

    // This toggle only ever offers the immediate types (the pre-order flow
    // hides it entirely), so reaching it means the guest has left pre-ordering
    // behind — drop the performance with it rather than shipping a stale id.
    setIsTheaterPreorder(false);
    setTheaterPerformanceId(null);
    setOrderType(type);

    if (restaurant?.allowAdvanceOrder) {
      setPickup('later');
    }
  };

  const handleCommentsChange = (newComments) => {
    setComments(newComments);
  };

  const handleDeliveryCommentChange = (newDeliveryComment) => {
    setDeliveryComment(newDeliveryComment);
  };

  const handleViewCart = () => {
    if (restaurant.orderingPaused) {
      showOrderingPaused();
      return;
    }

    if (!restaurant.isMenuBuyable) {
      return;
    }

    if (!venueSellsOnline(restaurant)) {
      return;
    }

    if (toggleOrderIfNotAllowed()) {
      setOrderNotAllowed(true);
      return;
    }

    if (checkIfCategoryIsDisabled()) {
      setMenuNotAvailable(true);
      return;
    }

    if (isUserAllowedToOrder) {
      if (orderItems.length === 0) {
        alert(`${translations[language]?.restaurant_menu_empty_cart}!`);
        return;
      }

      // Calculate initial tip amount
      const initialTotal = calculateTotal(
        true,
        hasBag,
        hasDeliveryFee,
        restaurant?.EnableWoltDelivery,
      );
      setTipAmount(parseFloat(tipPercentage?.percentage || 0) * initialTotal);

      // Navigate to review step
      navigateToStep('review');
    } else {
      orderContext.setLoginModal(true);
      orderContext.setShouldShowReview(true);
    }
  };

  const getEarliestTimeForPickupNow = () => {
    // For today, consider current time + lagTime
    const now = new Date(); // current date & time
    const earliestAvailable = new Date(
      now.getTime() + (orderType === 'delivery' ? 60 : lagTime) * 60000,
    );

    // Round up to nearest lag time
    earliestAvailable.setMinutes(
      Math.ceil(earliestAvailable.getMinutes() / pickupTimeInterval) * pickupTimeInterval,
      0,
      0,
    );

    // Format as HH:mm
    const timeString = moment(earliestAvailable).format('HH:mm');

    // Compare with restaurant's earliest available time
    const restaurantEarliestTime = getEarliestTime(
      restaurant,
      orderType === 'delivery' ? 60 : lagTime,
      pickupTimeInterval,
      new Date(),
      selectedCategory,
    );

    // Use the later of the two times
    if (timeString > restaurantEarliestTime) {
      return timeString;
    }

    return restaurantEarliestTime;
  };

  // Calculate tip amount when tipPercentage or total changes
  useEffect(() => {
    const orderTotal = calculateTotal(true, hasBag, hasDeliveryFee, restaurant?.EnableWoltDelivery);
    setTipAmount(parseFloat(tipPercentage?.percentage || 0) * orderTotal);

    // Set tipId based on percentage for backend
    if (parseFloat(tipPercentage?.percentage || 0) === 0) {
      setTipId(null);
    } else {
      // This would normally come from your tip options data
      // Just setting a placeholder ID for now based on percentage
      setTipId(tipPercentage?.value);
    }
    // calculateTotal is a plain function, so the discount amount has to be a
    // dependency of its own or the tip is left computed on the pre-discount base.
  }, [tipPercentage, orderItems, hasBag, hasDeliveryFee, discountApplied.amount]);

  // The first slot a guest can order ahead for on/after `fromDate`: at least
  // the lead time away, allowed by the picker's own list, and inside the
  // venue's hours by the server's rule. See utils/pickupSlots.
  const findFirstPickupSlot = (fromDate = null) => {
    const leadMinutes = orderType === 'delivery' ? 60 : lagTime;
    return firstPickupSlot({
      availability: restaurant?.availability,
      now: new Date(),
      leadMinutes,
      interval: pickupTimeInterval,
      fromDate,
      disabledTimesFor: (day) =>
        getDisabledTimes(day, restaurant, leadMinutes, pickupTimeInterval, selectedCategory),
    });
  };

  // Which DAY an advance order starts on. It used to be "today, unless now +
  // lead time rounded UP TO THE LEAD TIME crosses midnight" — for delivery that
  // rounded 23:50 to 24:00 and moved the date to tomorrow while the venue was
  // still open, and it never looked at the venue's hours at all.
  useEffect(() => {
    if (!lagTime || !restaurant) return;

    const slot = findFirstPickupSlot();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const keepRestoredDay = restoredScheduleRef.current?.orderType === orderType;

    if (slot && slot.date.getTime() > today.getTime()) {
      if (!keepRestoredDay) setPickupDate(slot.date);
      setIsPickupForTomorrow(true);
    } else if (orderType === 'delivery') {
      // Delivery switches the lead time to 1 hour, so the date has to snap back
      // to today. Restricted to delivery on purpose: resetting unconditionally
      // also wiped a restored take-away pre-order date when the customer came
      // back from a failed payment (?persistData=1 re-applies orderType, which
      // re-runs this effect).
      if (!keepRestoredDay) setPickupDate(new Date());
      setIsPickupForTomorrow(false);
    }
    // Keyed on the venue's id, not the object: the object is rebuilt on a
    // language switch, which must not throw away the date the guest chose.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lagTime, orderType, restaurant?._id]);

  // Which TIME, for the day in pickupDate. The old version took today's
  // "now + lead" and kept it whenever it compared later than the venue's
  // earliest slot — on ANY date, and without checking it against closing time.
  // That is how delivery at 22:50 offered tomorrow 23:50 at a venue that closes
  // at 23:30. When the chosen day has nothing left (past closing, or a closed
  // day) the date rolls over to the next open day's first slot.
  useEffect(() => {
    if (!restaurant || !pickupDate) return;

    const restored = restoredScheduleRef.current;
    if (
      restored &&
      (restored.orderType !== orderType || !moment(pickupDate).isSame(restored.pickupDate, 'day'))
    ) {
      restoredScheduleRef.current = null;
    }

    const slot = findFirstPickupSlot(pickupDate);
    if (!slot) return;

    if (!moment(slot.date).isSame(pickupDate, 'day')) {
      // Re-runs this effect for the new day, which then sets the time.
      setPickupDate(slot.date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      setIsPickupForTomorrow(slot.date.getTime() > today.getTime());
      return;
    }

    // The restored time stands while it can still be ordered for.
    if (restoredScheduleRef.current && pickupTime >= slot.time) return;

    const nextPickupTime = slot.time;
    setPickupTime(nextPickupTime);

    // A scheduled delivery was quoted for a date and a time, and this just
    // moved one or both. Re-quote for the new slot — unless the quote held is
    // already for it (a restored one, say). ASAP quotes carry no slot.
    if (
      orderType === 'delivery' &&
      pickup === 'later' &&
      address?.street &&
      woltQuoteMeta?.slot !== woltQuoteSlot('later', pickupDate, nextPickupTime)
    ) {
      handleCheckWoltDeliveryBlur(null, nextPickupTime);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [restaurant, pickupDate, lagTime, orderType]);

  // Prepare extras items for the counter component
  const extrasItems =
    selectedItem?.extras?.map((extra) => ({
      id: extra._id,
      label: extra.name,
      price: extra.price,
      initialCount: 0,
    })) || [];

  // Group attributes by their type
  const groupedAttributes =
    selectedItem?.attributes?.reduce((acc, attribute) => {
      // Skip attributes that belong to specific options
      if (attribute.belongsto) return acc;

      // Add id property that is the same as _id
      const attributeWithId = {
        ...attribute,
        id: attribute._id,
      };

      // Convert options to the format needed by our UI components
      const options = attribute.options?.map((option) => ({
        ...option,
        id: option._id,
        name: option.name,
        additionalPrice: option.additionalPrice,
        price: option.additionalPrice || 0,
        priceLabel: option.additionalPrice
          ? `${restaurant?.currency?.code || 'DKK'} ${option.additionalPrice}`
          : '',
        isconnectedto: option?.isconnectedto || null,
      }));

      if (options && options.length > 0) {
        acc.push({
          ...attributeWithId,
          options,
        });
      }

      return acc;
    }, []) || [];

  // Modal Footer Component
  const ItemSelectionFooter = useCallback(() => {
    return (
      <div className="bg-white !p-3 sm:!p-6 border-t-[1px] border-[#f5f5f5] rounded-b-2xl">
      {addStockError && (
        <p className="text-red-500 text-sm !mb-3 text-center sm:text-left">{addStockError}</p>
      )}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-0">
        <div className="w-full max-w-full sm:max-w-[250px] flex items-center justify-between">
          <Counter
            className="p-2 border border-[#f3f3f3] rounded-[50px]"
            initialValue={itemQuantity}
            value={itemQuantity}
            onChange={(next) => {
              setAddStockError('');
              setItemQuantity(next);
            }}
          />
          <p className="font-bold text-sm sm:text-base">
            {restaurant?.currency?.code || 'DKK'} {calculateModalTotalPrice().toFixed(2)}
          </p>
        </div>
        <button
          className={`w-full sm:w-auto bg-primary text-xs sm:text-sm text-white font-bold !px-2 py-[10px] sm:!px-4 sm:py-[15px] rounded-full hover:bg-secondary transition-colors ${
            itemQuantity <= 0 && 'bg-gray-300 hover:bg-gray-400 cursor-not-allowed'
          }`}
          onClick={handleAddToOrder}
          disabled={itemQuantity <= 0 || checkingAdd}
        >
          <i className="fa fa-plus mr-2" style={{ fontSize: 12 }} />
          {editingCartId
            ? translations[language]?.restaurant_menu_order_item_edit
            : translations[language]?.restaurant_menu_order_item_add}
        </button>
      </div>
      </div>
    );
  }, [
    addStockError,
    checkingAdd,
    itemQuantity,
    restaurant?.currency?.code,
    calculateModalTotalPrice,
    handleAddToOrder,
    editingCartId,
    translations,
    language,
  ]);

  // Start - OrderInformation Functions
  const handleTableNumberChange = useCallback((value) => {
    setTableNumber(value);
  }, []);

  // Single-table venues: pre-select the only table so dine-in guests are not
  // asked to open a one-option dropdown. Dine-in only — the payload sends
  // tableNumber regardless of order type, so a take-away order must not pick
  // one up. Runs as an effect rather than inline in the fetch so it re-applies
  // after resetOrderInfo() clears the table for the next order.
  useEffect(() => {
    if (orderType === 'dine-in') {
      if (tableNumbers.length === 1 && !tableNumber) {
        setTableNumber(tableNumbers[0]);
      }
    } else if (tableNumber) {
      // Leaving dine-in drops the table: nothing outside dine-in reads it, but
      // the payload sends it unconditionally, so a stale value would follow a
      // take-away order into POS.
      setTableNumber(null);
    }
  }, [orderType, tableNumbers, tableNumber]);

  const handleTipPercentageChange = useCallback((value) => {
    setTipPercentage(value);
  }, []);

  const handlePhoneNumberChange = useCallback((value) => {
    setPhoneNumber(value);
  }, []);

  const handleDiscountCodeChange = useCallback((value) => {
    setDiscountCode(value);
    // Editing the code invalidates the previous quote the instant the guest
    // touches the field. Waiting for the debounce would leave the old code's
    // amount on the total while a different code is being typed. The status goes
    // back to idle rather than checking, because 'checking' shows the spinner and
    // an in-flight Apply also locks the field — neither belongs mid-keystroke.
    setDiscountApplied(EMPTY_DISCOUNT);
    setDiscountError('');
    setDiscountStatus('idle');
  }, []);

  // Apply / Enter: nothing to compute here, it just tells the lookup to stop
  // waiting out its debounce and ask the server now.
  const handleApplyDiscount = useCallback(() => {
    setDiscountApplyNonce((nonce) => nonce + 1);
  }, []);

  const handleRemoveDiscount = useCallback(() => {
    setDiscountCode('');
    setDiscountApplied(EMPTY_DISCOUNT);
    setDiscountError('');
    setDiscountStatus('idle');
  }, []);

  // The one place a discount amount ever enters this page: the answer from
  // /discountcode/validate. It always reports — including a zero — so a code
  // that stopped applying (the qualifying item was removed, the cart changed,
  // the lookup failed) can never leave a stale amount behind and quote the guest
  // less than they are charged.
  const handleDiscountResult = useCallback((result) => {
    // An idle report means "the field is empty, there is nothing to check". It
    // must not wipe an order-level rejection: handleDiscountCodeRejected empties
    // the field on purpose and leaves the reason on screen for the guest to read.
    // 'unsupported' is sticky for the same reason — the endpoint does not appear
    // mid-session, and un-hiding the field would only offer it again for nothing.
    if (result?.status === 'idle') {
      setDiscountApplied(EMPTY_DISCOUNT);
      setDiscountStatus((current) =>
        current === 'error' || current === 'unsupported' ? current : 'idle',
      );
      return;
    }

    setDiscountStatus(result?.status || 'idle');
    setDiscountError(result?.valid ? '' : result?.message || '');
    setDiscountApplied(
      result?.valid
        ? {
            valid: true,
            code: result.code,
            amount: Number(result.amount) || 0,
            type: result.type || null,
            value: Number(result.value) || 0,
          }
        : EMPTY_DISCOUNT,
    );
  }, []);

  // The order was refused because of the code. Clear it, show the reason and put
  // the guest back on the review step. Never retry, never resubmit a payment —
  // the guest presses Pay again and the order goes through at full price.
  const handleDiscountCodeRejected = useCallback(
    (discountCodeError, message) => {
      setDiscountCode('');
      setDiscountApplied(EMPTY_DISCOUNT);
      setDiscountStatus('error');
      setDiscountError(
        discountMessage(language, discountCodeError?.messageKey, discountCodeError?.meta, message),
      );
      navigateToStep('review');
    },
    // navigateToStep is a stable page helper declared above; language is the
    // only value that can change the copy.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [language],
  );

  // Theater mode: the order was refused because of the chosen performance —
  // either the gate rejected the id (expired, ordering window closed — HTTP
  // 400/409 with { Message }) or the capacity gate found the performance full
  // (HTTP 429, message supplied by the caller since the server's is about the
  // restaurant, not the show). Clear the choice, re-fetch the list so gone
  // performances disappear, show the reason and put the guest back on review
  // to pick another. Never retry.
  const handleTheaterPerformanceRejected = useCallback(
    (message) => {
      setTheaterPerformanceId(null);
      fetchTheaterPerformances();
      setErrorMessage(message || translations[language]?.order_information_theater_rejected || '');
      setIsErrorModalOpen(true);
      navigateToStep('review');
    },
    // navigateToStep is a stable page helper; fetchTheaterPerformances is a
    // useCallback keyed on restaurantId + the flag.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [language, fetchTheaterPerformances],
  );

  // Theater mode: refresh the program on arrival at review. The picker lives
  // on this step, and the list behind it may have been fetched when the guest
  // first opened the menu — an hour and several courses ago. Together with the
  // hook's foreground refetch this is what keeps a closed performance from
  // still being pickable at checkout.
  useEffect(() => {
    if (!restaurant?.TheaterModeEnabled) return;
    if (currentStep !== 'review') return;
    fetchTheaterPerformances();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep, restaurant?.TheaterModeEnabled]);

  // Theater mode: a refresh can drop the performance the guest had chosen —
  // its ordering window closed, or the venue pulled it from the program. Say
  // so and clear the choice: leaving it set gives a picker with nothing
  // checked and a cart summary whose performance row silently vanished, with
  // no way for the guest to know why. Two guards matter here. An empty list is
  // never treated as "gone", because the hook fails open to [] on a network
  // error and a hiccup must not wipe a valid choice. And nothing fires while
  // the confirmation modal is up: that order is already placed, and the
  // modal reads the same selection to show which performance it was for.
  useEffect(() => {
    if (!restaurant?.TheaterModeEnabled) return;
    if (isProcessingOrderModalOpen) return;
    if (!theaterPerformanceId || !theaterPerformances.length) return;
    if (theaterPerformances.some((p) => p.id === theaterPerformanceId)) return;
    setTheaterPerformanceId(null);
    setErrorMessage(translations[language]?.order_information_theater_rejected || '');
    setIsErrorModalOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theaterPerformances, theaterPerformanceId, isProcessingOrderModalOpen, language]);

  // Deep link — <web>/restaurants/<id>?code=SOMMER25, the link the admin Share
  // popover hands out (§7.5). Applied exactly once, and only once the cart has
  // something in it: validating an empty basket returns `empty_cart`, which is an
  // error the guest cannot act on before they have ordered anything.
  const discountDeepLinkRef = useRef(false);
  useEffect(() => {
    if (discountDeepLinkRef.current) return;
    if (!orderItems.length) return;

    discountDeepLinkRef.current = true;

    // Never override a code the guest typed (or one restored after a redirect).
    if (discountCode) return;

    const linked = new URL(window.location.href).searchParams.get('code');
    if (!linked) return;

    setDiscountCode(String(linked).toUpperCase());
    setDiscountStatus('checking');
  }, [orderItems.length, discountCode]);

  // A code belongs to exactly one restaurant, and a code with no cart has
  // nothing to discount. Clear it in both cases so a stale quote can never ride
  // into a different order. The ref guard keeps the restaurant check from firing
  // on mount, where it would wipe a restored or deep-linked code.
  const discountRestaurantRef = useRef(restaurantId);
  useEffect(() => {
    if (discountRestaurantRef.current === restaurantId) return;
    discountRestaurantRef.current = restaurantId;
    discountDeepLinkRef.current = false;
    handleRemoveDiscount();
  }, [restaurantId, handleRemoveDiscount]);

  useEffect(() => {
    if (orderItems.length === 0 && discountCode) handleRemoveDiscount();
  }, [orderItems.length, discountCode, handleRemoveDiscount]);

  const handlePickupChange = (pickup) => {
    if (toggleOrderIfNotAllowed(null, pickup)) {
      setOrderNotAllowed(true);
      return;
    }

    // Checked before quoting: a venue without advance orders stays on 'now',
    // and quoting 'later' for it would book an ASAP order as scheduled.
    if (pickup !== 'now' && !restaurant.allowAdvanceOrder) {
      return;
    }

    if (orderType === 'delivery') {
      handleCheckWoltDeliveryBlur(null, null, pickup);
    }

    if (pickup == 'now') {
      setPickupTime(getEarliestTimeForPickupNow());
      setPickup('now');
    } else {
      setPickup('later');
    }
  };

  // A delivery quote is for one date. Dropping it here keeps Next from going
  // ahead on it; the pickup-time effect below fetches the new one once the
  // time for the new date is known.
  const handlePickupDateChange = useCallback(
    (date) => {
      setPickupDate(date);
      if (orderType === 'delivery') {
        invalidateWoltQuote();
      }
    },
    // invalidateWoltQuote only touches setters and a ref
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [orderType],
  );

  const handlePickupTimeChange = (time) => {
    setPickupTime(time);

    if (orderType === 'delivery') {
      handleCheckWoltDeliveryBlur(null, time);
    }
  };

  const handleRoomNumberChange = useCallback((roomNumber) => {
    setRoomNumber(roomNumber);
  }, []);
  // End - OrderInformation Functions

  // Start - Navigation for OrderInformation & YourItems
  const handleNextStep = async () => {
    // Validate the form fields
    let isOrderInfoValid = orderInfoRef.current.validateForm();
    let isAddressValid = true;
    let isGuestInfoValid = true;

    let timeValidationResult = { isValid: true };
    if (yourItemsRef.current) {
      timeValidationResult = yourItemsRef.current.validateTimeAvailability();
    }

    if (restaurant?.EnableWoltDelivery && orderType === 'delivery') {
      isAddressValid = addressRef.current?.validateForm();
    }

    if (isGuest) {
      isGuestInfoValid = guestInfoRef.current.validateForm(true);
    }

    if (handleCheckMinimumAmountValidation()) return;

    if (!timeValidationResult.isValid) {
      setValidationErrorMessage(
        timeValidationResult.errorMessage || 'Some items are not available at the selected time',
      );
      setIsValidationErrorModalOpen(true);
      return;
    }

    // A discount check in flight holds EMPTY_DISCOUNT, so leaving review now
    // would send the order with no code at all and charge full price without
    // saying so. Wait for the quote to settle instead.
    if (discountStatus === 'checking') return;

    // Only proceed to next step if validation passes
    if (isOrderInfoValid && isGuestInfoValid && isAddressValid && timeValidationResult.isValid) {
      if (currentStep === 'review') {
        //the venue's delivery minimum, measured against the cart as it stands
        //right now. The quantity buttons in YourItems stay live on this step, so
        //a quote fetched for a bigger basket would otherwise carry a short order
        //through to payment — and the backend only gates the quote, not the order.
        if (orderType === 'delivery' && !woltMinOrder.met) {
          setIsErrorModalOpen(true);
          setErrorMessage(woltMinOrder.message);
          return;
        }

        //a discount check in flight holds EMPTY_DISCOUNT, so leaving review now
        //would send the order with no code at all and charge full price without
        //saying so. Wait for the quote to settle instead.
        if (discountStatus === 'checking') {
          return;
        }

        //the quote is nulled whenever what it was for changes, so a delivery
        //order can reach this point without one — and would then be silently
        //never booked with Wolt. The same goes for a quote Wolt no longer
        //honours, and for one that failed last time: pressing Next is the
        //guest's retry. Re-quote here and hard-stop if it still fails.
        if (
          orderType === 'delivery' &&
          restaurant?.EnableWoltDelivery &&
          (woltDeliveryError.error || !isWoltQuoteFresh(woltDelivery, woltQuoteMeta?.fetchedAt))
        ) {
          const requote = await requireWoltQuote();
          if (!requote) {
            return;
          }
        }

        // Stock: refuse to move on with a basket the venue cannot serve. The
        // reservation at checkout is still the real guard — this just means the
        // guest does not fill in card details for an order that will be refused.
        if (stockCheck.hasIssue) {
          setIsErrorModalOpen(true);
          setErrorMessage(stockCheck.message);
          return;
        }

        navigateToStep('payment');

        if (isGuest) {
          await registerGuestUser();
        }
      } else if (currentStep === 'payment') {
        handlePayment();
      }
    }
  };

  const handlePrevStep = () => {
    if (currentStep === 'review') {
      navigateToStep('menu');
    } else if (currentStep === 'payment') {
      navigateToStep('review');
    }
  };

  const handleCheckMinimumAmountValidation = () => {
    let minimumAmountOfTotalForASpecificRestaurantValid = 199;
    let isMinimumOfTotalForASpecificRestaurantValid =
      grandTotal() >= minimumAmountOfTotalForASpecificRestaurantValid;

    if (
      !isMinimumOfTotalForASpecificRestaurantValid &&
      restaurantId === '68dfd70c12f97d001a434919'
    ) {
      setValidationErrorMessage(
        `${translations[language]?.minimum_amount_message} ${
          restaurant?.currency?.code || 'DKK'
        } ${minimumAmountOfTotalForASpecificRestaurantValid}`,
      );

      setIsValidationErrorModalOpen(true);
      return true;
    }

    return false;
  };

  const handlePayment = () => {
    if (orderItems.length === 0) {
      alert(`${translations[language]?.restaurant_menu_empty_cart}!`);
      return;
    }

    setIsCheckoutModalOpen(false);
  };

  const registerGuestUser = async () => {
    const payload = {
      firstname: restaurant?._id === '69bd59b55a10b5001b234d1d' ? 'heywaitr' : guestInfo.name,
      lastname: '',
      phone: phoneNumber,
      email: guestInfo.email || '',
    };

    const response = await axios({
      method: 'POST',
      url: `${configData.SERVER_URL}/orders/guest/createuser`,
      data: payload,
    });

    // Update the guest id to be from the db id
    orderContext.setGuestId(response.data._id.toString());
    orderContext.setIsGuest(true);
    localStorage.setItem('guest-uui', response.data._id.toString());

    return response.data;
  };

  const handleGoToSelectPaymentMethod = () => {
    paymentRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  // End - Navigation for OrderInformation & YourItems

  // Start - Payment Processing Functions
  const handleProcessPayment = async (paymentMethod, orderDetails) => {
    setLoading(true);

    try {
      const isDineIn = orderType === 'dine-in';
      const isAdvanceOrder = !isDineIn && pickup === 'later';
      // Recomputed here rather than closed over, for the same reason isDineIn is.
      const isOrderNowAtTheater = isTheaterOrderNowFlow(restaurant, isTheaterPreorder, orderType);

      // If order details are provided, it means the payment has already been processed
      if (orderDetails) {
        const placedOrder = markDeliveryOrder(orderDetails, orderType, address, userProfile?.token);
        setPayConfirm(placedOrder);
        setIsProcessingOrderModalOpen(true);
        navigateToStep('menu');
        setLoading(false);

        if (!placedOrder.status) {
          startPendingOrderPolling(placedOrder);
        } else if (placedOrder.status === 'confirmed') {
          startConfirmedOrderPolling(placedOrder);
        }

        return;
      }

      // Check if this is a ParkTable payment
      if (paymentMethod === 'parktable') {
        // Format orders for the API
        const formattedOrders = formatOrdersForCheckout(orderItems);

        const currentDate = moment(pickupDate).format('MM/DD/YYYY');
        const currentTime = moment(pickupTime, 'HH:mm').format('h:mm A');
        const formattedDateTime = moment(currentDate + ' ' + currentTime).format();

        const orderPayload = {
          restaurantId: restaurant._id,
          date: new Date(),
          isDineIn: isDineIn,
          mobilenumber: phoneNumber,
          notes: comments,
          tableNumber: tableNumber ? tableNumber?.value : '',
          promoCodeId: '',
          // Guest discount code. The code STRING and nothing else — the server
          // re-validates it, computes the amount and reserves the redemption.
          // Never overloaded onto promoCodeId, which is an unvalidated client
          // string written straight through. Omitted when no code is applied, so
          // an API that does not know the field sees the payload it sees today.
          discountCode: discountApplied.valid ? discountApplied.code : undefined,
          // In-cart stamp voucher redemption (§9.3). Client sends ONLY the voucher
          // code + chosen reward; the server validates/reserves and constructs the
          // reward line (never isReward/price from the client). Omitted when nothing
          // is staged so the order is unaffected.
          redeemVoucher:
            stagedVoucher?.voucher?.Code && stagedVoucher?.rewardProductId
              ? {
                  voucherCode: stagedVoucher.voucher.Code,
                  rewardProductId: stagedVoucher.rewardProductId,
                }
              : undefined,
          payment_method: paymentMethod,
          tipId: tipId,
          isweb: true,
          isAdvanceOrder: isAdvanceOrder,
          advanceOrderDate: isAdvanceOrder ? formattedDateTime : undefined,
          // Theater mode — the two keys, decided in one place shared with
          // Payment.js (utils/orderTypes.theaterOrderFields).
          ...theaterOrderFields({
            isTheaterPreorder,
            isTheaterOrderNow: isOrderNowAtTheater,
            isDineIn,
            theaterPerformanceId,
          }),
          order: formattedOrders,
          bagtakeawayprice:
            bagTakeAwayPriceFor({ restaurant, orderType, isDineIn, hasBag }),
          deliveryfee:
            restaurant?.HasDeliveryFee && !isDineIn && hasDeliveryFee
              ? restaurant?.DeliveryFee || 0
              : 0,
          isParkTable: paymentMethod === 'parktable',
          takeAwayRoom: restaurant?.AskTakeAwayRoom && !isDineIn ? roomNumber : '',
          isGuest: isGuest,
          guestId: orderContext.guestId || '',
          // woltShipment / deliveryComment / deliveryAddress / isDelivery — only
          // a delivery order carries a shipment, and isDelivery lets the backend
          // reject one that lost its quote (utils/woltDelivery.woltOrderFields).
          ...woltOrderFields({
            orderType,
            restaurant,
            woltDelivery,
            deliveryComment,
            deliveryAddress: {
              street: address.street,
              city: address.city,
              post_code: address.zipcode,
            },
          }),
          source: 'web',
        };

        let url = `${configData.SERVER_URL}/orders/v2/preorder`;

        const response = await axios({
          method: 'POST',
          url,
          data: orderPayload,
          headers: {
            Authorization: `Bearer ${userProfile?.token || ''}`,
          },
        });

        setLoading(false);
        // Clear cart
        resetOrderInfo();

        // Voucher redemption is consumed by this order (server reserved it); clear
        // the staged voucher and re-pull wallet + progress to reflect the server. §9.3
        clearStagedVoucher?.();
        refreshAfterOrder?.(restaurant?._id);

        // Save order info to cache for status tracking
        if (response.data) {
          if (response.data?.order_details) {
            saveOrderInfoToCache(response.data.order_details);
          }
        }
      }
    } catch (error) {
      setLoading(false);

      // Ordering paused after the page loaded: show the server's reason in the
      // error modal rather than an alert.
      if (subscriptionBlockedFromError(error)) {
        showOrderingPaused(serverMessage(error));
        return;
      }

      // The order could not be placed with this discount code (it ran out, the
      // guest already used it, the window closed, …). Checked BEFORE the 429
      // branch on purpose: `rate_limited` is also a 429, and treating it as a
      // capacity problem would open the wrong modal.
      const discountCodeError = error?.response?.data?.discountCodeError;
      if (discountCodeError) {
        handleDiscountCodeRejected(discountCodeError, error?.response?.data?.Message);
        return;
      }

      // Sold out — before the theater branch, same reasoning as Payment.js: both
      // are 400/409 with a Message, and a sold-out cola was clearing the guest's
      // chosen performance.
      if (soldOutFromError(error)) {
        alert(guestFacingError(error));
        return;
      }

      // Theater mode: the chosen performance was refused (400/409 + { Message }).
      // Same contract as Payment.js — only when an id was actually sent.
      // The component-scoped flags, not the try-scoped copies: this catch sits
      // outside that block, which is why `isDineIn` here has always been the
      // outer one too.
      if (
        sendsTheaterPerformance({
          isTheaterPreorder,
          isTheaterOrderNow,
          isDineIn,
        }) &&
        theaterPerformanceId &&
        [400, 409].includes(error?.response?.status)
      ) {
        handleTheaterPerformanceRejected(error?.response?.data?.Message);
        return;
      }

      // Theater PRE-ORDER: a 429 would mean the chosen performance is full.
      // `nextAvailableSlot` is a clock time the guest cannot act on — the
      // pre-order has no pickup time picker — so route it to the performance
      // picker with a localized reason instead of opening the slot modal.
      // Only the pre-order: the API skips its capacity gate for pre-orders
      // but applies it to an order for now at a theater like any walk-in, so
      // that 429 means the venue is busy and belongs to the generic modal
      // below, not to "choose another performance". Checked BEFORE it.
      if (
        isTheaterPreorder &&
        !isDineIn &&
        theaterPerformanceId &&
        error?.response?.status === 429
      ) {
        handleTheaterPerformanceRejected(
          translations[language]?.order_information_theater_full,
        );
        return;
      }

      if (error?.response?.status === 429) {
        setCapacityFullModal({
          open: true,
          nextSlot: error?.response?.data?.nextAvailableSlot || null,
        });
        return;
      }
      console.error('Payment processing error:', error);
      // Reads the response body, not the AxiosError — see utils/serverError.js.
      alert(guestFacingError(error));
    }
  };

  const saveOrderInfoToCache = (orderDetails) => {
    const placedOrder = markDeliveryOrder(orderDetails, orderType, address, userProfile?.token);
    localStorage.setItem('hw_pending_orderv2', JSON.stringify(placedOrder));

    setPayConfirm(placedOrder);
    setIsProcessingOrderModalOpen(true);
    navigateToStep('menu');
    setLoading(false);

    if (!placedOrder.status) {
      startPendingOrderPolling(placedOrder);
    } else if (placedOrder.status === 'confirmed') {
      startConfirmedOrderPolling(placedOrder);
    }
  };

  const handlePersistOrderData = (guestId) => {
    const data = {
      orderType,
      pickup,
      pickupDate,
      pickupTime,
      // Theater mode: survives the MobilePay/Vipps redirect like the pickup
      // time does; re-validated by the backend when the order is placed. The
      // FLOW travels with it — a theater venue now sells both for now and for a
      // performance, and restoring the id without the flag would come back as
      // an ordinary order that silently drops the performance at checkout.
      theaterPerformanceId,
      isTheaterPreorder,
      phoneNumber,
      tableNumber,
      comments,
      deliveryComment,
      // Wolt delivery: the address and the quote the guest was shown, with its
      // age, so a failed payment or an added card comes back to the same fee —
      // and, once the quote has lapsed, to a fresh one rather than a stale one.
      address,
      woltDelivery: orderType === 'delivery' ? woltDelivery : null,
      woltQuoteMeta: orderType === 'delivery' ? woltQuoteMeta : null,
      tipId,
      tipPercentage,
      restaurantId: restaurant?._id,
      hasBag,
      hasDeliveryFee,
      roomNumber,
      orderItems,
      currentStep,
      guestInfo,
      guestId,
      // Only the code STRING survives the MobilePay/Vipps/Apple/Google Pay
      // redirect — never the amount. It is re-validated on the way back, so a
      // code that ran out while the guest was at the payment provider cannot be
      // restored as a discount that no longer exists.
      discountCode: discountApplied.valid ? discountApplied.code : '',
    };

    persistOrderData(data);
  };

  const resetOrderInfo = () => {
    setOrderItems([]);
    localStorage.removeItem('hw_cart');
    setComments('');
    setDeliveryComment('');
    navigateToStep('menu');
    setTipPercentage(null);
    if (!userProfile?.token) {
      setPhoneNumber('');
    }
    setTableNumber(null);
    invalidateWoltQuote();
  };

  const addNewCard = async () => {
    setLoading(true);

    // Persist current order state
    handlePersistOrderData(orderContext.guestId);

    try {
      let root_url = window.location.origin
        ? window.location.origin + '/'
        : window.location.protocol + '//' + window.location.host + '/';

      const response = await axios({
        method: 'GET',
        url: `${configData.SERVER_URL}/order/addcard?successuri=${root_url}api/redirect?persistData=1&failuri=${root_url}api/redirect?persistData=1&restaurant=${restaurant?._id}`,
        headers: {
          Authorization: `Bearer ${userProfile?.token}`,
        },
      });

      // Redirect to card addition page
      window.location.replace(response.data);
    } catch (error) {
      console.error('Error adding new card:', error);
      setLoading(false);
    }
  };

  const deleteCardConfirmation = (cardId) => {
    setIsDeleteCardModalOpen(true);
    setDeleteCardModalMessage(translations[language]?.your_items_delete_card);
    setDeleteCardId(cardId);
  };

  const deleteCard = async () => {
    setInAppLoading(true);

    try {
      await axios({
        method: 'DELETE',
        url: `${configData.SERVER_URL}/user/deletecard?card=${deleteCardId}`,
        headers: {
          Authorization: `Bearer ${userProfile?.token}`,
        },
      });

      setMyCards(myCards?.filter((card) => card.id !== deleteCardId) || []);
      setDeleteCardId(null);
      setInAppLoading(false);
    } catch (error) {
      console.error('Error deleting card:', error);
      setIsErrorModalOpen(true);
      setErrorMessage('Error!');
      setDeleteCardId(null);
      setInAppLoading(false);
    }
  };

  const formatOrdersForCheckout = (items) => {
    return items.map((item) => {
      // Process extras
      const extras = (item.extras || [])
        .filter((extra) => extra.quantity > 0)
        .map((extra) => ({
          _id: extra.id,
          qty: extra.quantity,
        }));

      // Process attributes
      const attributes = [];
      if (item.selectedAttributes && item.selectedAttributes.length > 0) {
        item.selectedAttributes.forEach((attr) => {
          if (attr.options && attr.options.length > 0) {
            attr.options.forEach((option) => {
              attributes.push({
                _id: attr.attributeId,
                value: option.id,
                qty: option.quantity || 1,
                isMultiple: attr?.isMultiple || false,
              });
            });
          }
        });
      }

      return {
        _id: item._id,
        lineId: item.cartId,
        qty: item.quantity,
        extras: extras,
        attributes: attributes,
        notes: item.comments || '',
      };
    });
  };
  // End - Payment Processing Functions

  const handleViewOpeningTimesOpen = useCallback(() => {
    setIsViewOpeningTimesOpen(true);
  }, []);

  const checkIfCategoryIsDisabled = () => {
    // Ordering is held to the exact serving window, judged at the moment being
    // ordered for — the same rule the server enforces in its menu-hours gate,
    // so a guest is never allowed to buy something checkout will refuse. This
    // replaces `selectedCategory.isAvailable`, which the server computed for
    // "right now" and the page then cached for the whole session: a tab left
    // open past a category's closing time kept selling it.
    const selectedCategoryClosed =
      !isEmpty(selectedCategory) && !isHigherCategoryOpenAt(selectedCategory, orderDateTime);

    // A theater PRE-ORDER sells while the venue is closed — the server's
    // menu-hours gate exempts it too, so the page must not block what checkout
    // would accept. The same venue's "order now" is an ordinary sale and gets
    // no exemption: that is what keeps a walk-in from buying a lunch category
    // at 22:00 just because the venue happens to run a theater programme.
    // Advance take-away is judged against its own slot, since orderDateTime
    // already resolves to the pickup moment rather than to now.
    if (isTheaterPreorder) {
      return false;
    }

    if (orderType === 'take-away' && restaurant.allowAdvanceOrder) {
      return noCategoryServable || selectedCategoryClosed;
    }

    // No category selected at all. `noCategoryServable` means the venue groups
    // its menu into higher categories but serves none of them on this day —
    // without it the flat "no higher categories" layout sold the whole card.
    if (isEmpty(selectedCategory)) {
      return noCategoryServable || !restaurant.isOpen;
    }

    if (orderType !== 'dine-in' && pickup === 'later') {
      return selectedCategoryClosed;
    }

    return selectedCategoryClosed || !restaurant.isOpen;
  };

  const isOpenThisTime = () => {
    if (!restaurant) return false;

    const pureTime = moment().format('HH:mm');
    const selectedDate = moment().format('MM/DD/YYYY');
    const dateTime = moment(selectedDate + ' ' + pureTime, 'MM/DD/YYYY HH:mm');

    return getRestaurantStatus(restaurant, true, dateTime) && restaurant.isOpen;
  };

  const toggleOrderIfNotAllowed = (updatedOrderType, updatedPickup) => {
    let newType = orderType;
    let newPickup = pickup;

    if (updatedOrderType) {
      newType = updatedOrderType;
    }

    if (updatedPickup) {
      newPickup = updatedPickup;
    }

    if (!isOpenThisTime()) {
      // A pre-order is bought for a performance days away — being shut right
      // now says nothing about whether it can be sold. This is the ONE exempt
      // path; every other tile is held to the venue's hours, including at a
      // theater, which is what lets a theater sell to walk-ins at all.
      //
      // Callers come in two shapes: the choose-order tiles name a tile, while
      // the cart and checkout re-check the flow already in progress with no
      // arguments at all. The second shape only ever sees 'take-away' for a
      // pre-order, so without the flag a closed theater would refuse its own
      // guest at "view cart" — the exemption has to cover both.
      if (newType === THEATER_TILE || (!updatedOrderType && isTheaterPreorder)) {
        return false;
      }

      if (newType === 'take-away' && !restaurant.allowAdvanceOrder) {
        return true;
      }

      if (newType === 'dine-in') {
        return true;
      }

      if (newPickup === 'now') {
        return true;
      }
    }

    return false;
  };

  const toggleRestaurantTakeAwayUnavailable = () => {
    if (!restaurant?.istakeout) {
      return true;
    }

    return false;
  };

  const handleOrderTypeSelectionContinue = useCallback(
    (tile) => {
      if (restaurant?.orderingPaused) {
        showOrderingPaused();
        return;
      }

      if (toggleOrderIfNotAllowed(tile)) {
        setOrderNotAllowed(true);
        return;
      }

      /*
       * Tickets leave the ordering flow entirely. A ticket purchase is its own
       * order with its own checkout — no table, no pickup slot, no menu — so
       * there is no order type to set and nothing here to carry forward. Sending
       * it through the steps below would put the guest in a food basket.
       */
      if (tile === TICKETS_TILE) {
        navigate(`/events/${restaurantId}`);
        return;
      }

      if (tile === 'delivery') {
        handleBagToggle(true);
      } else {
        // Same as handleOrderTypeChange: no Wolt quote outlives delivery.
        invalidateWoltQuote();
      }

      const isPreorder = tile === THEATER_TILE;
      setIsTheaterPreorder(isPreorder);
      setOrderType(orderTypeForTile(tile));

      // Picking "Bestil nu" after landing on a pre-order (or the reverse) must
      // not carry the other flow's choice along: a stale performance id would
      // turn an order for now back into one held until curtain.
      if (!isPreorder) {
        setTheaterPerformanceId(null);
      }

      navigateToStep('menu');

      // Neither theater flow has a pickup slot to choose — the performance IS
      // the slot, and the server derives the time from it. Anything left on
      // 'later' would put the date/time picker back beside the performance
      // list and mark the order as a generic advance order.
      if (restaurant?.TheaterModeEnabled) {
        setPickup('now');
      } else if (restaurant?.allowAdvanceOrder) {
        setPickup('later');
      }
    },
    [toggleOrderIfNotAllowed, navigateToStep, navigate, restaurantId, restaurant, showOrderingPaused],
  );

  useEffect(() => {
    if (restaurant?._id && !isRestoredNavigationRef.current) {
      // Ordering paused: the guest stays on the menu to browse. No steering
      // into the ordering flow and no modal on arrival — the gates above say
      // why when the guest tries to order.
      if (restaurant?.orderingPaused) {
        return;
      }

      // A venue that sells nothing at all sends the guest back to the list with
      // "this place doesn't take orders". A theater running ONLY pre-orders has
      // both immediate flags off and is not that venue — see venueSellsOnline.
      if (!venueSellsOnline(restaurant)) {
        setOrderFromRestaurantsNotAllowed(true);
        return;
      }

      if (!isOpenThisTime()) {
        // ONCE per venue. This effect steers a guest who ARRIVES at a closed
        // venue; it must never fire again afterwards, or it yanks them out of
        // whatever step they have reached. It re-runs on every change to the
        // performance list, and the programme is refetched on arrival at
        // review (and whenever the tab returns to the foreground) — so
        // pressing "Se kurv og bestil" at a closed theater threw the guest,
        // and their basket, straight back to the tile picker.
        //
        // The lock waits for the programme, because until it lands we cannot
        // tell "still loading" from "nothing on sale", and that decides
        // whether there is a pre-order path at all.
        const programmeKnown =
          !restaurant?.TheaterModeEnabled || hasFetchedTheaterPerformances;
        if (closedLandingRef.current === restaurant._id && programmeKnown) return;
        if (programmeKnown) closedLandingRef.current = restaurant._id;

        navigateReplace('choose-order');

        // A closed theater can still sell its programme, so put the guest on
        // the pre-order flow rather than on an immediate tile the gate would
        // refuse, and skip the "we're closed" modal — it is not true, and it
        // would talk the guest out of an order the venue is standing by to
        // take. The immediate tiles carry their own "Åbner kl. ..." line.
        //
        // Unless there is nothing to pre-order: an empty or failed programme
        // drops that tile, and then the venue really is closed to this guest.
        const hasPreorderPath =
          restaurant?.TheaterModeEnabled &&
          (!hasFetchedTheaterPerformances || theaterPerformances.length > 0);

        // Nothing on sale yet, but a date to name for when it will be: the
        // pre-order tile is on screen saying so. The "we're closed, come back
        // during opening hours" modal would answer a question the guest did
        // not ask — their next visit is decided by the programme, not by the
        // bar's hours — so it is left to the tile, which is the accurate one.
        const preorderOpensLater =
          restaurant?.TheaterModeEnabled &&
          hasFetchedTheaterPerformances &&
          theaterPerformances.length === 0 &&
          Boolean(theaterOpensDate);

        if (hasPreorderPath) {
          setOrderType('take-away');
          setIsTheaterPreorder(true);
          setPickup('now');
        } else if (!preorderOpensLater) {
          setOrderNotAllowed(true);
        }

        if (!restaurant?.TheaterModeEnabled && restaurant?.allowAdvanceOrder) {
          setOrderType('take-away');
          setPickup('later');
        }
      }
    }
  }, [
    orderType,
    pickup,
    restaurant,
    hasFetchedTheaterPerformances,
    theaterPerformances,
    theaterOpensDate,
  ]);

  // Theater mode: the chosen performance object, for the summary rows in the
  // cart and the confirmation modal. null when nothing is chosen (or the id
  // fell out of the orderable list on a re-fetch).
  const selectedTheaterPerformance =
    theaterPerformances.find((p) => p.id === theaterPerformanceId) || null;

  const isGuest = useMemo(() => {
    return (
      !userProfile.token &&
      orderContext.isGuest &&
      localStorage.getItem('guest-uui') !== undefined &&
      localStorage.getItem('guest-uui') !== null
    );
  }, [userProfile, orderContext.isGuest]);

  // The base the tip-dropdown labels are computed from. `discountApplied.amount`
  // is a mandatory dependency: calculateTotal is a plain function, so without it
  // the dropdown keeps offering a percentage of the pre-discount subtotal.
  const memoizedBaseAmount = useMemo(
    () => calculateTotal(true, hasBag, hasDeliveryFee, restaurant?.EnableWoltDelivery),
    [hasBag, hasDeliveryFee, orderItems, restaurant?.EnableWoltDelivery, discountApplied.amount],
  );

  // The cart exactly as checkout posts it — no prices, the server prices every
  // line. Memoized on the cart so the lookup effect sees one stable fingerprint
  // per cart state instead of a new array on every render.
  const discountOrder = useMemo(
    () => formatOrdersForCheckout(orderItems),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [orderItems],
  );

  // The /discountcode/validate lookup lives HERE, on the page, and not inside the
  // field. The field is drawn by OrderInformation inside ReviewStep, and React
  // Router renders only the matching route — so the whole review subtree unmounts
  // the moment the guest steps on to `payment`. YourItems does not: it is
  // rendered outside <Routes> on both steps and keeps its live −/+ quantity
  // buttons, so the cart can still change with nothing watching it, and the
  // server-quoted amount would stay pinned to a basket that no longer exists.
  // From here the lookup is alive for the whole checkout — including the return
  // from a MobilePay/Vipps redirect, which lands on `payment` and must
  // re-validate the restored code.
  //
  // `restaurant` and `guestInfo` go in whole and the hook resolves the contact
  // and the currency itself. That is not tidiness: this component sits on the
  // eslint-plugin-react-hooks 4.3.0 cliff, where a single extra `||` or `?.` in
  // its own body makes the plugin report later hooks as conditionally called and
  // the CRA build fail. Keep this call branch-free.
  const discountLocked = useDiscountCodeLookup({
    restaurantId,
    code: discountCode,
    order: discountOrder,
    mobilenumber: phoneNumber,
    guestInfo,
    restaurant,
    isGuest,
    guestId: orderContext.guestId,
    isDineIn: orderType === 'dine-in',
    applyNonce: discountApplyNonce,
    onResult: handleDiscountResult,
  });

  const isUserAllowedToOrder =
    (userProfile !== undefined && userProfile !== null && userProfile.profile) || isGuest;

  const isDineIn = orderType === 'dine-in';
  const isAdvanceOrder = !isDineIn && pickup === 'later';
  const fromHeyCharity = Boolean(restaurantId === '68809191653ace001970bb8f' || false) || false;

  // The other theater flow: an immediate order at a theater, which asks which
  // performance the guest is here for and puts that on the ticket in place of a
  // table number (a theater has no tables). Unlike a pre-order it changes no
  // timing at all — the server is told so explicitly with theaterOrderNow, and
  // prints it straight away.
  const isTheaterOrderNow = isTheaterOrderNowFlow(restaurant, isTheaterPreorder, orderType);

  const isParkTable =
    restaurant?._id === '69bd59b55a10b5001b234d1d'
      ? restaurant?.HasParkTable
      : isDineIn &&
        tableNumber &&
        restaurant?.HasParkTable &&
        ((Array.isArray(tableNumbers) &&
          tableNumbers.find((t) => t.number === tableNumber?.value)?.isParkTable) ||
          tableNumber?.isParkTable === true);

  // The tiles as the guest sees them right now: the venue's own list, minus a
  // pre-order tile with nothing to sell, with the immediate tiles greyed out
  // while the venue is shut. Derived at render rather than stored, because
  // "is it open" changes without anything re-fetching.
  const visibleOrderTypeOptions = useMemo(() => {
    // eslint-disable-next-line no-unused-expressions
    openingTick;
    const withProgramme = withTheaterAvailability(orderTypeOptions, {
      hasFetchedPerformances: hasFetchedTheaterPerformances,
      performanceCount: theaterPerformances.length,
      opensDate: theaterOpensDate,
      language,
    });

    return withClosedState(withProgramme, {
      isOpenNow: isOpenThisTime(),
      restaurant,
      language,
      nextOpening: getNextOpeningSlot(restaurant?.availability),
    });
  }, [
    orderTypeOptions,
    hasFetchedTheaterPerformances,
    theaterPerformances,
    theaterOpensDate,
    restaurant,
    language,
    openingTick,
  ]);

  // The checkout and cart toggles list the immediate types only: switching to a
  // pre-order means choosing a performance, which lives on the choose-order
  // step, not in a two-state toggle.
  const immediateOrderTypeOptions = useMemo(
    () => orderTypeOptions.filter((option) => option.id !== THEATER_TILE && option.id !== TICKETS_TILE),
    [orderTypeOptions],
  );

  const ChooseOrderStep = useMemo(
    () => (
      <OrderTypeSelection
        orderTypeOptions={visibleOrderTypeOptions}
        onContinue={handleOrderTypeSelectionContinue}
      />
    ),
    [visibleOrderTypeOptions, handleOrderTypeSelectionContinue],
  );

  const MenuStep = useMemo(
    () => (
      <>
        <RestaurantMenuHeader
          restaurant={restaurant}
          activeGroup={activeGroup}
          setActiveGroup={setActiveGroup}
          onViewOpeningTimesOpen={handleViewOpeningTimesOpen}
        />

        {activeGroup === 'order-now' ? (
          <>
            {hasHigherCategories && (
              <CategoryTabs
                categories={categories}
                selectedCategory={selectedCategory}
                onSelectCategory={handleCategorySelect}
              />
            )}

            <SubCategories
              ref={subNavRef}
              subCategories={currentSubCategories}
              activeSubCategory={activeSubCategory}
              onSelectSubCategory={handleSubCategorySelect}
              hasHigherCategories={hasHigherCategories}
            />

            <MenuGrid
              restaurant={restaurant}
              categories={categories}
              subCategories={filteredSubCategories}
              groupedItems={filteredGroupedMenuItems}
              defaultImg={DEFAULT_IMG}
              onSelectItem={handleItemSelectionModalOpen}
              activeSubCategory={activeSubCategory}
              hasHigherCategories={hasHigherCategories}
              progressByProduct={progressByProduct || {}}
            />
          </>
        ) : activeGroup === 'information' ? (
          <div className="py-[10px] md:py-[20px] lg:py-[30px] px-[15px] md:px-[25px] lg:px-[40px] min-h-[30vh]">
            {restaurantDescription ? (
              <div className="space-y-1">
                <h1 className="text-base sm:text-lg font-bold">
                  {translations[language]?.restaurant_menu_description_title}
                </h1>
                <h2 className="text-sm sm:text-base whitespace-pre-wrap">
                  {restaurantDescription}
                </h2>
              </div>
            ) : (
              <h2 className="text-center">
                {translations[language]?.restaurant_menu_no_description}
              </h2>
            )}
          </div>
        ) : null}
      </>
    ),
    [
      restaurant,
      activeGroup,
      categories,
      selectedCategory,
      currentSubCategories,
      filteredSubCategories,
      activeSubCategory,
      hasHigherCategories,
      filteredGroupedMenuItems,
      handleCategorySelect,
      handleSubCategorySelect,
      handleItemSelectionModalOpen,
      // MANDATORY: the stampcard badges on the menu cards come from this map,
      // which arrives asynchronously ({} -> data). Without it the badges only
      // appear after some unrelated dependency happens to change.
      progressByProduct,
      translations,
      language,
      subNavRef,
    ],
  );

  const ReviewStep = useMemo(
    () => (
      <div className="space-y-[16px] lg:space-y-[75px] order-[2] md:order-[1]">
        {isGuest && (
          <div className="restaurant-menu-section rounded-xl">
            <GuestInformation
              ref={guestInfoRef}
              guestInfo={guestInfo}
              setGuestInfo={setGuestInfo}
              phoneNumber={phoneNumber}
              onPhoneNumberChange={handlePhoneNumberChange}
              isValidPhone={isValidPhone}
              setIsValidPhone={setIsValidPhone}
              restaurant={restaurant}
              fromHeyCharity={fromHeyCharity}
              tableNumber={tableNumber}
              orderType={orderType}
              isParkTable={tableNumber ? isParkTable : Boolean(restaurant?.HasParkTable)}
            />
          </div>
        )}

        <div className="restaurant-menu-section rounded-xl">
          <OrderInformation
            restaurant={restaurant}
            restaurantId={restaurantId}
            orderType={orderType}
            orderTypeOptions={immediateOrderTypeOptions}
            onOrderTypeChange={handleOrderTypeChange}
            tableNumber={tableNumber}
            onTableNumberChange={handleTableNumberChange}
            tipId={tipId}
            tipPercentage={tipPercentage?.percentage || 0}
            onTipPercentageChange={handleTipPercentageChange}
            phoneNumber={phoneNumber}
            onPhoneNumberChange={handlePhoneNumberChange}
            comments={comments}
            onCommentsChange={handleCommentsChange}
            deliveryComment={deliveryComment}
            onDeliveryCommentChange={handleDeliveryCommentChange}
            discountCode={discountCode}
            onDiscountCodeChange={handleDiscountCodeChange}
            onApplyDiscount={handleApplyDiscount}
            onRemoveDiscount={handleRemoveDiscount}
            discountLocked={discountLocked}
            discountStatus={discountStatus}
            discountAmount={discountApplied.amount}
            discountError={discountError}
            currency={restaurant?.currency?.code || 'DKK'}
            baseAmount={memoizedBaseAmount}
            isValidPhone={isValidPhone}
            setIsValidPhone={setIsValidPhone}
            pickup={pickup}
            pickupOptions={pickupOptions}
            onPickupChange={handlePickupChange}
            pickupDate={pickupDate}
            onPickupDateChange={handlePickupDateChange}
            pickupTime={pickupTime}
            onPickupTimeChange={handlePickupTimeChange}
            isPickupForTomorrow={isPickupForTomorrow}
            disabledTimes={getDisabledTimes(
              pickupDate,
              restaurant,
              orderType === 'delivery' ? 60 : lagTime,
              pickupTimeInterval,
              selectedCategory,
            )}
            pickupTimeInterval={pickupTimeInterval}
            theaterPerformances={theaterPerformances}
            theaterPerformanceId={theaterPerformanceId}
            onTheaterPerformanceChange={setTheaterPerformanceId}
            isTheaterPreorder={isTheaterPreorder}
            isTheaterOrderNow={isTheaterOrderNow}
            roomNumber={roomNumber}
            onRoomNumberChange={handleRoomNumberChange}
            hasBag={hasBag}
            onBagToggle={handleBagToggle}
            bagFee={bagFee}
            hasDeliveryFee={hasDeliveryFee}
            onDeliveryFeeToggle={handleDeliveryFeeToggle}
            deliveryFee={deliveryFee}
            woltDelivery={woltDelivery}
            setWoltDelivery={setWoltDelivery}
            setWoltDeliveryFee={setWoltDeliveryFee}
            onWoltDeliveryFeeToggle={handleWoltDeliveryFeeToggle}
            woltDeliveryFee={woltDeliveryFee}
            woltMinOrder={woltMinOrder}
            onCheckWoltDeliveryBlur={handleCheckWoltDeliveryBlur}
            addressRef={addressRef}
            address={address}
            setAddress={setAddress}
            onWoltAddressEdit={invalidateWoltQuote}
            tableNumbers={tableNumbers}
            restaurantTips={restaurantTips}
            ref={orderInfoRef}
            isGuest={isGuest}
            guestId={orderContext.guestId}
            fromHeyCharity={fromHeyCharity}
            setIsErrorModalOpen={setIsErrorModalOpen}
            setErrorMessage={setErrorMessage}
            isParkTable={tableNumber ? isParkTable : Boolean(restaurant?.HasParkTable)}
          />
        </div>
      </div>
    ),
    [
      restaurant,
      restaurantId,
      orderType,
      immediateOrderTypeOptions,
      isTheaterPreorder,
      isTheaterOrderNow,
      handleOrderTypeChange,
      tableNumber,
      handleTableNumberChange,
      tipId,
      tipPercentage,
      handleTipPercentageChange,
      phoneNumber,
      handlePhoneNumberChange,
      comments,
      handleCommentsChange,
      deliveryComment,
      handleDeliveryCommentChange,
      discountCode,
      handleDiscountCodeChange,
      handleApplyDiscount,
      handleRemoveDiscount,
      discountLocked,
      discountStatus,
      discountApplied.amount,
      discountError,
      memoizedBaseAmount,
      isValidPhone,
      pickup,
      pickupOptions,
      handlePickupChange,
      pickupDate,
      handlePickupDateChange,
      pickupTime,
      handlePickupTimeChange,
      isPickupForTomorrow,
      pickupTimeInterval,
      theaterPerformances,
      theaterPerformanceId,
      selectedCategory,
      roomNumber,
      handleRoomNumberChange,
      hasBag,
      handleBagToggle,
      bagFee,
      hasDeliveryFee,
      handleDeliveryFeeToggle,
      deliveryFee,
      woltDelivery,
      setWoltDelivery,
      setWoltDeliveryFee,
      handleWoltDeliveryFeeToggle,
      woltDeliveryFee,
      woltMinOrder,
      handleCheckWoltDeliveryBlur,
      address,
      setAddress,
      invalidateWoltQuote,
      tableNumbers,
      restaurantTips,
      isGuest,
      orderContext.guestId,
      guestInfo,
      setGuestInfo,
      setIsValidPhone,
      fromHeyCharity,
      setIsErrorModalOpen,
      setErrorMessage,
      isParkTable,
    ],
  );

  const PaymentStep = useMemo(
    () => (
      <div ref={paymentRef}>
        <Payment
          orderItems={orderItems}
          setOrderItems={setOrderItems}
          totalPrice={grandTotal()}
          currency={restaurant?.currency?.code || 'DKK'}
          restaurant={restaurant}
          userProfile={userProfile}
          onPayment={handleProcessPayment}
          myCards={myCards}
          setMyCards={setMyCards}
          onCapacityFull={(nextSlot) =>
            setCapacityFullModal({ open: true, nextSlot: nextSlot || null })
          }
          addNewCard={addNewCard}
          deleteCardConfirmation={deleteCardConfirmation}
          onPersistOrderData={handlePersistOrderData}
          resetOrderInfo={resetOrderInfo}
          formatOrdersForCheckout={formatOrdersForCheckout}
          isDineIn={isDineIn}
          tableNumbers={tableNumbers}
          tableNumber={tableNumber}
          setTableNumber={setTableNumber}
          tipAmount={tipAmount}
          bagFee={bagCharge}
          deliveryFee={deliveryFee}
          hasBag={bagCharge > 0}
          hasDeliveryFee={hasDeliveryFee}
          orderType={orderType}
          woltDelivery={woltDelivery}
          woltDeliveryFee={woltDeliveryFee}
          woltQuoteFetchedAt={woltQuoteMeta?.fetchedAt || null}
          woltMinOrder={woltMinOrder}
          onRequireWoltQuote={requireWoltQuote}
          deliveryAddress={
            orderType === 'delivery'
              ? { street: address.street, city: address.city, post_code: address.zipcode }
              : null
          }
          tipId={tipId}
          comments={comments}
          setComments={setComments}
          deliveryComment={deliveryComment}
          phoneNumber={phoneNumber}
          isGuest={isGuest}
          guestId={orderContext.guestId}
          pickup={pickup}
          pickupDate={pickupDate}
          pickupTime={pickupTime}
          roomNumber={roomNumber}
          currentStep={currentStep}
          navigateToStep={navigateToStep}
          tipPercentage={tipPercentage}
          setTipPercentage={setTipPercentage}
          onCheckMinimumAmountValidation={handleCheckMinimumAmountValidation}
          loyaltyEmail={
            guestInfo?.email || userProfile?.profile?.email || userProfile?.profile?.Email
          }
          loyaltyPhone={phoneNumber || userProfile?.profile?.mobilenumber}
          onLoyaltyDiscount={setLoyaltyDiscountPercent}
          discountCode={discountApplied.valid ? discountApplied.code : ''}
          discountAmount={discountApplied.amount}
          onDiscountCodeRejected={handleDiscountCodeRejected}
          theaterPerformanceId={theaterPerformanceId}
          isTheaterPreorder={isTheaterPreorder}
          isTheaterOrderNow={isTheaterOrderNow}
          onTheaterPerformanceRejected={handleTheaterPerformanceRejected}
          onOrderingPaused={showOrderingPaused}
        />
      </div>
    ),
    [
      orderItems,
      setOrderItems,
      grandTotal,
      restaurant,
      userProfile,
      handleProcessPayment,
      myCards,
      setMyCards,
      addNewCard,
      deleteCardConfirmation,
      handlePersistOrderData,
      resetOrderInfo,
      formatOrdersForCheckout,
      isDineIn,
      tableNumbers,
      tableNumber,
      setTableNumber,
      tipAmount,
      bagFee,
      deliveryFee,
      hasBag,
      hasDeliveryFee,
      orderType,
      woltDelivery,
      woltDeliveryFee,
      woltQuoteMeta,
      woltMinOrder,
      address,
      tipId,
      comments,
      setComments,
      deliveryComment,
      phoneNumber,
      isGuest,
      guestInfo?.email,
      orderContext.guestId,
      pickup,
      pickupDate,
      pickupTime,
      roomNumber,
      currentStep,
      navigateToStep,
      tipPercentage,
      setTipPercentage,
      handleCheckMinimumAmountValidation,
      // MANDATORY: this memo passes totalPrice={grandTotal()}, which is a plain
      // function call. Without the discount amount here the payment step keeps
      // working off a stale, pre-discount total.
      discountApplied.valid,
      discountApplied.code,
      discountApplied.amount,
      handleDiscountCodeRejected,
      theaterPerformanceId,
      handleTheaterPerformanceRejected,
      showOrderingPaused,
    ],
  );

  if (loading) return <Loader />;
  if (error) return <div>{translations[language]?.restaurant_menu_no_data}</div>;
  if (!restaurant) return null;
  if (restaurant.orderingPaused) {
    return (
      <OrderingPausedScreen message={restaurant.orderingPausedMessage} />
    );
  }

  // HeyWaitr+ member pricing for the selected item shown in the item modal.
  // Precomputed (rather than inlined in JSX) to keep the conditional simple.
  const selectedItemHasMemberPrice =
    selectedItem?.oldPrice != null && Number(selectedItem.oldPrice) > Number(selectedItem.price);

  return (
    <div
      className="restaurant-menu-container max-w-[80rem] mx-auto p-2 sm:p-4 !pb-[120px] sm:!pb-[100px]"
      style={{
        '--primary': restaurant?.PrimaryColor || '#96c11f',
        '--secondary': restaurant?.SecondaryColor || '#6f9800',
        '--text': restaurant?.TextColor || '#ffffff',
      }}
    >
      {inAppLoading && <Loader />}

      {/* Routes for choose-order and menu steps only */}
      <Routes>
        <Route path="choose-order" element={ChooseOrderStep} />
        <Route path="menu" element={MenuStep} />
        <Route path="" element={<Navigate to="menu" replace />} />
      </Routes>

      {/* Conditional layout with routes for review and payment steps */}
      {(currentStep === 'review' || currentStep === 'payment') && (
        <div className="grid grid-cols-1 md:grid-cols-[auto_300px] lg:grid-cols-[auto_454px] gap-[16px] lg:gap-[75px] items-start">
          <div className="space-y-[16px] lg:space-y-[75px] order-[2] md:order-[1]">
            <Routes>
              <Route path="review" element={ReviewStep} />
              <Route path="payment" element={PaymentStep} />
            </Routes>
          </div>

          <div className="restaurant-menu-section rounded-xl order-[1] md:order-[2]">
            <YourItems
              restaurant={restaurant}
              categories={categories}
              items={orderItems}
              soldOutItems={stockCheck.soldOut}
              onUpdateQuantity={handleUpdateItemQuantity}
              onRemoveItem={handleRemoveFromOrder}
              onEditItem={editCartItem}
              tipPercentage={tipPercentage?.percentage || 0}
              tipAmount={tipAmount}
              discountCode={discountApplied.valid ? discountApplied.code : ''}
              discountAmount={discountApplied.valid ? Number(discountApplied.amount) || 0 : 0}
              total={calculateTotal(true, true, true, true)}
              grandTotal={grandTotal()}
              grandTotalBeforeCode={grandTotal(true)}
              calculatedServiceFee={calculatedServiceFee()}
              currency={restaurant?.currency?.code || 'DKK'}
              onPayment={handlePayment}
              defaultImg={DEFAULT_IMG}
              orderType={orderType}
              hasBag={bagCharge > 0}
              bagFee={bagCharge}
              hasDeliveryFee={hasDeliveryFee}
              deliveryFee={deliveryFee}
              woltDelivery={woltDelivery}
              woltDeliveryFee={woltDeliveryFee}
              hasServiceFee={hasServiceFee}
              serviceFee={serviceFee}
              hasFlatFee={hasFlatFee}
              flatFee={flatFee}
              pickupDate={pickupDate}
              pickupTime={pickupTime}
              pickup={pickup}
              theaterPerformance={selectedTheaterPerformance}
              isTheaterPreorder={isTheaterPreorder}
              isTheaterOrderNow={isTheaterOrderNow}
              currentStep={currentStep}
              onGoToSelectPaymentMethod={handleGoToSelectPaymentMethod}
              loyaltyEmail={
                guestInfo?.email || userProfile?.profile?.email || userProfile?.profile?.Email
              }
              loyaltyPhone={phoneNumber || userProfile?.profile?.mobilenumber}
              onLoyaltyDiscount={setLoyaltyDiscountPercent}
              ref={yourItemsRef}
            />
          </div>
        </div>
      )}

      <OrderSummary
        items={orderItems}
        grandTotal={
          computeDiscountedTotal({
            // The SAME base YourItems computes its member discount from
            // (`total={calculateTotal(true, true, true, true)}`), or the sticky
            // summary bar and the panel quote two different totals. Four
            // arguments: tip, bag, delivery fee and the Wolt delivery fee all
            // excluded. The fifth, excludeDiscountCode, is left at its default so
            // the member discount is taken off the post-code subtotal, which is
            // what the panel does too.
            total: calculateTotal(true, true, true, true),
            grandTotal: grandTotal(),
            loyaltyPercent: loyaltyDiscountPercent,
            // The `|| 0` is load-bearing beyond the coercion: react-hooks 4.3.0
            // mis-analyses this component when this expression has no branch in
            // it, and reports every hook below line ~2500 as conditional. See the
            // note in utils/stampcard.js.
            voucherDiscount: Number(voucherDiscount) || 0,
          }).adjustedGrandTotal
        }
        onViewCart={handleViewCart}
        orderType={orderType}
        orderTypeOptions={immediateOrderTypeOptions}
        onOrderTypeChange={handleOrderTypeChange}
        isTheaterPreorder={isTheaterPreorder}
        restaurant={restaurant}
        currentStep={currentStep}
        onNextStep={handleNextStep}
        onPrevStep={handlePrevStep}
        onGoToSelectPaymentMethod={handleGoToSelectPaymentMethod}
        fromHeyCharity={fromHeyCharity}
        woltFeePending={isWoltFeePending(orderType, restaurant, woltDeliveryFee)}
      />

      {/* Modals */}
      {selectedItem && (
        <Modal
          isOpen={isItemSelectionModalOpen}
          onClose={() => setIsItemSelectionModalOpen(false)}
          headerImg={selectedItem?.image || DEFAULT_IMG || restaurant?.MenuPlaceholder}
          FooterComponent={ItemSelectionFooter}
          noPadding
        >
          <div className="flex flex-col">
            <div className="p-4 space-y-2 sm:space-y-4 border-b-[1px] border-[#f5f5f5]">
              <h1 className="text-lg sm:text-xl font-semibold">{selectedItem?.name}</h1>
              <p className="text-sm text-[#707070] whitespace-pre-wrap">
                {selectedItem?.description}
              </p>
              <h2 className="text-sm font-semibold">
                {selectedItemHasMemberPrice ? (
                  <span className="inline-flex items-center gap-2">
                    <span className="text-[#96c11f]">
                      {restaurant?.currency?.code || 'DKK'} {selectedItem?.price}
                    </span>
                    <span className="text-gray-400 font-normal line-through">
                      {restaurant?.currency?.code || 'DKK'} {selectedItem?.oldPrice}
                    </span>
                    {selectedItem?.premiumDiscount != null && (
                      <span className="text-[11px] font-bold text-white bg-[#96c11f] rounded px-1.5 py-0.5">
                        -{selectedItem.premiumDiscount}%
                      </span>
                    )}
                  </span>
                ) : (
                  <>
                    {restaurant?.currency?.code || 'DKK'} {selectedItem?.price}
                  </>
                )}
              </h2>

              {/* Stampcard hint (§9.2): which card this product earns on, live
                  progress, and the potential stamps for the CHOSEN quantity —
                  honestly capped when the campaign limits stamps per order. */}
              {(() => {
                const sp = progressByProduct?.[selectedItem?._id || selectedItem?.id];
                const maxCount = Number(sp?.maxCount);
                if (!sp || !Number.isFinite(maxCount) || maxCount <= 0) return null;
                const current = Number(sp.currentCount) || 0;
                const inProgress = current % maxCount;
                const cap = sp.stampsPerOrderCap != null ? Number(sp.stampsPerOrderCap) : null;
                const qty = Math.max(1, Number(itemQuantity) || 1);
                const potential = cap != null ? Math.min(qty, cap) : qty;
                const after = Math.min(inProgress + potential, maxCount);
                return (
                  <StampProgressCard
                    name={sp.stampName || translations[language]?.stamp_card || 'Klippekort'}
                    current={inProgress}
                    after={after}
                    maxCount={maxCount}
                    potential={potential}
                    capNote={
                      cap != null && qty > cap
                        ? `${translations[language]?.stamp_cap_note || 'max'} ${cap} ${translations[language]?.stamp_per_order || 'pr. ordre'}`
                        : null
                    }
                    completes={after >= maxCount}
                  />
                );
              })()}
            </div>

            {extrasItems.length > 0 && (
              <div className="p-4 border-b-[1px] border-[#f5f5f5]">
                <h2 className="text-md lg:text-lg font-semibold mb-4">
                  {translations[language]?.restaurant_menu_options}
                </h2>
                <MultiCounter
                  items={extrasItems}
                  onChange={handleExtrasChange}
                  currency={restaurant?.currency?.code || 'DKK'}
                  selectedValues={selectedExtras}
                  totalSelectedCount={0} // Extras don't count towards the total limit
                />
              </div>
            )}

            {groupedAttributes.map((attribute) => {
              // Find attribute in selectedAttributes
              const attributeSelection = selectedAttributes.find(
                (attr) => attr.attributeId?.toString() === attribute._id?.toString(),
              );
              const selectedOptions = attributeSelection ? attributeSelection.options : [];
              const isRequired = attribute.attributesRequired;

              // Handle connected attributes for options
              const connectedAttributes = selectedItem?.attributes
                ?.filter((attr) => {
                  if (!attr.belongsto) return false;

                  return selectedOptions.some((selection) => {
                    if (!selection?.isconnectedto || !Array.isArray(selection?.isconnectedto)) {
                      return false;
                    }

                    const attrIdStr = String(attr._id);
                    return selection?.isconnectedto.some(
                      (connectedId) => String(connectedId) === attrIdStr,
                    );
                  });
                })
                .map((attr) => {
                  const selectedOption = selectedOptions.find((selection) => {
                    if (!selection?.isconnectedto || !Array.isArray(selection?.isconnectedto)) {
                      return false;
                    }

                    const attrIdStr = String(attr._id);
                    return selection?.isconnectedto.some(
                      (connectedId) => String(connectedId) === attrIdStr,
                    );
                  });

                  if (selectedOption) {
                    return {
                      ...attr,
                      id: attr._id,
                    };
                  }

                  return {
                    ...attr,
                    id: attr._id,
                  };
                });

              return (
                <div key={attribute._id} className="p-4 border-b-[1px] border-[#f5f5f5]">
                  <h2 className="text-md lg:text-lg font-semibold mb-2">
                    {attribute.name}
                    {isRequired && (
                      <span className="text-sm text-red-500 ml-1">
                        &#40;{translations[language]?.restaurant_menu_required}&#41;
                      </span>
                    )}
                  </h2>

                  {attribute.isMultiple ? (
                    <MultiCounter
                      items={
                        attribute.options.map((option) => ({
                          _id: option.id,
                          id: option.id,
                          label: option.name,
                          price: option.additionalPrice,
                          initialCount: 0,
                          additionalPrice: option.additionalPrice,
                          limit: option.limit || null,
                          minlimit: option.minlimit || null,
                          isconnectedto: option?.isconnectedto || null,
                        })) || []
                      }
                      onChange={(newCounts, itemId) =>
                        handleMultipleCounterChange(attribute._id, itemId, newCounts)
                      }
                      currency={restaurant?.currency?.code || 'DKK'}
                      maxOptionCount={attribute.limitPerCategory}
                      selectedValues={selectedOptions}
                      attribute={attribute}
                      totalSelectedCount={
                        // Only sum up quantities from selectedAttributes
                        selectedAttributes?.reduce?.(
                          (total, attr) =>
                            total +
                            attr?.options?.reduce?.((sum, opt) => sum + (opt?.quantity || 1), 0),
                          0,
                        )
                      }
                      connectedAttributes={connectedAttributes}
                      onConnectedAttributeChange={handleRadioAttributeChange}
                      selectedAttributes={selectedAttributes}
                    />
                  ) : (
                    <RadioGroup
                      options={attribute.options}
                      onChange={(selectedOption) =>
                        handleRadioAttributeChange(attribute._id, selectedOption)
                      }
                      currency={restaurant?.currency?.code || 'DKK'}
                      required={isRequired}
                      selectedOption={selectedOptions.length > 0 ? selectedOptions[0] : null}
                      connectedAttributes={connectedAttributes}
                      onConnectedAttributeChange={handleRadioAttributeChange}
                      selectedAttributes={selectedAttributes}
                    />
                  )}
                </div>
              );
            })}

            <div className="p-4 border-b-[1px] border-[#f5f5f5]">
              <h2 className="text-md lg:text-lg font-semibold mb-4">
                {translations[language]?.comments}
              </h2>
              <textarea
                className="w-full !p-3 sm:!p-4 bg-[#f5f5f5] rounded-xl h-[150px] resize-none text-sm sm:text-base"
                placeholder={`${translations[language]?.comments_placeholder}...`}
                value={itemComments}
                onChange={(e) => setItemComments(e.target.value)}
              />
            </div>
          </div>
        </Modal>
      )}

      <RestaurantAvailabilityModal
        isOpen={isViewOpeningTimesOpen}
        onClose={() => setIsViewOpeningTimesOpen(false)}
        availability={restaurant?.availability}
      />

      <ValidationErrorModal
        isOpen={isValidationErrorModalOpen}
        onClose={() => {
          setIsValidationErrorModalOpen(false);
          setValidationErrorMessage('');
        }}
        validationErrorMessage={validationErrorMessage}
      />

      <MenuNotAvailableModal isOpen={menuNotAvailable} onClose={() => setMenuNotAvailable(false)} />
      <MenuNotAvailableModal
        isOpen={noMenuAvailable}
        onClose={() => setNoMenuAvailable(false)}
        messageKey="no_menu_available_modal_message"
      />

      <OrderNotAllowedModal
        isOpen={orderNotAllowed}
        onClose={() => setOrderNotAllowed(false)}
        allowAdvanceOrder={restaurant?.allowAdvanceOrder}
        isTakeAway={restaurant?.istakeout}
      />

      <RushModeModal
        restaurant={restaurant}
        isOpen={isRushModeModalOpen}
        onClose={() => setIsRushModeModalOpen(false)}
      />

      <CapacityFullModal
        isOpen={capacityFullModal.open}
        onClose={() => setCapacityFullModal({ open: false, nextSlot: null })}
        nextAvailableSlot={capacityFullModal.nextSlot}
        isDineIn={orderType === 'dine-in'}
        onPickAnotherTime={() => {
          setCapacityFullModal({ open: false, nextSlot: null });
          if (currentStep !== 'review') navigateToStep('review');
          // Defer scroll so the review step has mounted
          setTimeout(() => {
            orderInfoRef.current?.scrollToPickupTime?.();
          }, 200);
        }}
      />

      <PromoPopupModal
        restaurant={restaurant}
        isOpen={isPromoPopupModalOpen}
        onClose={() => setIsPromoPopupModalOpen(false)}
      />

      <ProcessingOrderModal
        isOpen={isProcessingOrderModalOpen}
        onClose={() => {
          // Don't allow closing if order is still pending
          if (!orderStatus?.isPending) {
            setIsProcessingOrderModalOpen(false);
          }
        }}
        title={translations[language]?.restaurant_menu_order_thank_you}
        noPadding
        noCloseButton={orderStatus?.isPending}
        restaurant={restaurant}
        orderType={orderType}
        payConfirm={payConfirm}
        orderStatus={orderStatus}
        cancelOrder={cancelOrder}
        isCancelling={isCancelling}
        theaterPerformance={receiptPerformance(payConfirm, selectedTheaterPerformance)}
      />

      <ErrorModal
        isOpen={isErrorModalOpen}
        onClose={() => {
          setIsErrorModalOpen(false);
          setErrorMessage('');
        }}
        errorMessage={errorMessage}
      />

      <DeleteCardModal
        message={deleteCardModalMessage}
        isOpen={isDeleteCardModalOpen}
        onConfirm={() => {
          deleteCard();
          setIsDeleteCardModalOpen(false);
          setDeleteCardModalMessage('');
        }}
        onClose={() => {
          setIsDeleteCardModalOpen(false);
          setDeleteCardModalMessage('');
          setDeleteCardId(null);
        }}
      />
    </div>
  );
}
